import { useState, useMemo, useCallback, useRef, useEffect, type CSSProperties } from "react";
import { Link } from "wouter";
import AdminLayout from "@/components/layout-admin";
import { useCleaningJobs, useCreateCleaningJob, useUpdateCleaningJob, useDeleteCleaningJob, useApartments, useAdminUsers, useBuildings, useGeneralTasks, useCreateGeneralTask, useUpdateGeneralTask, useDeleteGeneralTask, useQaReport, useCleaningJobFinancialRecords, useUpdateFinancialRecord, useOrgContracts, useOrganizations } from "@/hooks/use-resources";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Home, Clock, CheckCircle2, AlertCircle, PlayCircle, Sparkles, User, AlertTriangle, Minus, Calendar, ChevronLeft, ChevronRight, List, CalendarDays, BedDouble, Users, ImageIcon, Camera, Plus, Pencil, Search, Copy, ClipboardList, MapPin, Shield, ExternalLink, X, CheckSquare, Settings, Check, Receipt, DollarSign, Edit, RotateCcw, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { apiRequest } from "@/lib/queryClient";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { RecurringSeriesCard } from "@/components/recurring-series-card";
import { PhotoLightbox, useLightbox } from "@/components/photo-lightbox";
import { SearchableSelect } from "@/components/ui/searchable-select";
import LumiCleanerSuggest from "@/components/lumi-cleaner-suggest";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { RevertFinancialRecordDialog } from "@/components/revert-financial-record-dialog";
import { WeekOutOfRangeSection, DayOutOfRangeSection } from "@/components/calendar-out-of-range-section";
import { AdminCleaningDayView } from "@/components/admin-cleaning-day-view";
import type { CleaningJob, Apartment } from "@shared/routes";

type FilterStatus = "all" | "pending" | "in_progress" | "completed" | "cancelled";
type FilterPriority = "all" | "urgent" | "normal" | "low";
type ViewMode = "calendar" | "week" | "list" | "day";
type PageTab = "cleaning_jobs" | "general_tasks" | "settings";

const priorityOrder: Record<string, number> = { urgent: 0, normal: 1, low: 2 };

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatDateShort(d: Date) {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function hasSpecificTime(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d.getHours() !== 0 || d.getMinutes() !== 0;
}

// Accepts string (ISO) or Date object — Drizzle infers scheduledDate as Date, callers may pass either
function formatScheduledDateTime(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const dateFormatted = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  if (d.getHours() === 0 && d.getMinutes() === 0) return dateFormatted;
  const timeFormatted = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${dateFormatted} at ${timeFormatted}`;
}

export default function AdminCleaningJobs() {
  const [jobsPage, setJobsPage] = useState(0);
  const JOBS_PAGE_SIZE = 300;
  const { data: apartments = [] } = useApartments();
  const { data: buildings = [] } = useBuildings();
  const { data: users = [] } = useAdminUsers();
  const { data: orgContractsData } = useOrgContracts();
  const { data: organizationsData = [] } = useOrganizations();
  const activeServiceOrgIds = new Set((orgContractsData?.asProvider ?? []).filter((c: any) => c.status === 'active').map((c: any) => c.consumerOrgId as number));
  const createJob = useCreateCleaningJob();
  const updateJob = useUpdateCleaningJob();
  const deleteJob = useDeleteCleaningJob();
  const { data: cleaningJobTypesData = [] } = useQuery<any[]>({ queryKey: ['/api/admin/cleaning-job-types'] });
  const [newJobTypeName, setNewJobTypeName] = useState("");
  const [renamingTypeId, setRenamingTypeId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const { data: generalTasksData = [], isLoading: generalTasksLoading } = useGeneralTasks();
  const createGeneralTask = useCreateGeneralTask();
  const updateGeneralTask = useUpdateGeneralTask();
  const deleteGeneralTask = useDeleteGeneralTask();
  const { toast } = useToast();
  const [pageTab, setPageTab] = useState<PageTab>("cleaning_jobs");
  const [showCreateTaskDialog, setShowCreateTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const emptyTask = {
    title: "", description: "", assignedTo: null as number | null,
    scheduledAt: "", scheduledTime: "",
    address: "", apartmentId: null as number | null,
    taskType: "other", status: "pending",
    billable: false, billingAmount: "", billedToUserId: null as number | null,
    cleanerPayoutAmount: "", secondAssigneeId: null as number | null,
    // Recurrence (FEAT-041)
    recurring: false,
    recurrenceFrequency: "weekly" as "daily" | "weekly" | "biweekly" | "monthly",
    recurrenceDaysOfWeek: [] as number[],
    recurrenceDayOfMonth: 1,
    recurrenceTimeOfDay: "09:00",
    recurrenceEndDate: "",
  };
  const [newTask, setNewTask] = useState(emptyTask);
  const [taskStatusFilter, setTaskStatusFilter] = useState<"all" | "pending" | "in_progress" | "completed">("all");
  const [taskTypeFilter, setTaskTypeFilter] = useState<string>("all");

  // URL-persisted filters — survive navigation back
  const _sp = () => new URLSearchParams(window.location.search);
  const [filter, setFilter] = useState<FilterStatus>(() => (_sp().get("status") as FilterStatus) || "all");
  const [priorityFilter, setPriorityFilter] = useState<FilterPriority>(() => (_sp().get("priority") as FilterPriority) || "all");
  const [searchTerm, setSearchTerm] = useState(() => _sp().get("q") || "");
  const [propertyFilter, setPropertyFilter] = useState<string>(() => _sp().get("apt") || "all");
  const [pmClientFilter, setPmClientFilter] = useState<string>(() => _sp().get("pm") || "all");

  // Keep URL in sync whenever filters change (replaceState = no browser history entry)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    filter === "all" ? sp.delete("status") : sp.set("status", filter);
    priorityFilter === "all" ? sp.delete("priority") : sp.set("priority", priorityFilter);
    searchTerm ? sp.set("q", searchTerm) : sp.delete("q");
    propertyFilter === "all" ? sp.delete("apt") : sp.set("apt", propertyFilter);
    pmClientFilter === "all" ? sp.delete("pm") : sp.set("pm", pmClientFilter);
    const qs = sp.toString();
    window.history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
  }, [filter, priorityFilter, searchTerm, propertyFilter, pmClientFilter]);

  const [viewMode, setViewMode] = useState<ViewMode>("calendar");
  const [listDateFrom, setListDateFrom] = useState("");
  const [listDateTo, setListDateTo] = useState("");
  const [calendarOffset, setCalendarOffset] = useState(0);
  const [dayViewDate, setDayViewDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [weekViewOffset, setWeekViewOffset] = useState(0);

  const { data: orgSettings } = useQuery<any>({ queryKey: ["/api/admin/org-settings"] });
  const weekStartDay: number = orgSettings?.weekStartDay ?? 0;
  const { data: laundryJobs = [] } = useQuery<any[]>({ queryKey: ["/api/admin/laundry-jobs"] });
  const getLaundryJob = (cleaningJobId: number) => (laundryJobs as any[]).find((lj: any) => lj.cleaningJobId === cleaningJobId);

  const serverApartmentFilter = propertyFilter !== "all" ? Number(propertyFilter) : null;
  const { queryDateFrom, queryDateTo } = useMemo(() => {
    if (viewMode === "list") {
      return { queryDateFrom: listDateFrom || null, queryDateTo: listDateTo || null };
    }
    if (viewMode === "day") {
      const d = dayViewDate.toISOString().split('T')[0];
      return { queryDateFrom: d, queryDateTo: d };
    }
    if (viewMode === "week") {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const dow = now.getDay();
      const diff = (dow - weekStartDay + 7) % 7;
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - diff + weekViewOffset * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return { queryDateFrom: weekStart.toISOString().split('T')[0], queryDateTo: weekEnd.toISOString().split('T')[0] };
    }
    // calendar (month) view
    const year = new Date().getFullYear();
    const month = new Date().getMonth() + calendarOffset;
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return { queryDateFrom: start.toISOString().split('T')[0], queryDateTo: end.toISOString().split('T')[0] };
  }, [viewMode, listDateFrom, listDateTo, dayViewDate, weekViewOffset, calendarOffset]);
  // Calendar/week/day are date-bounded → load the full window (server caps at 1000), not a 300-row
  // page (which silently dropped jobs past row 300). Only the unbounded list view paginates.
  const isPaginatedView = viewMode === "list";
  const { data: jobsData, isLoading } = useCleaningJobs(isPaginatedView ? jobsPage : 0, isPaginatedView ? JOBS_PAGE_SIZE : 1000, serverApartmentFilter, queryDateFrom, queryDateTo);
  const jobs: any[] = jobsData?.jobs ?? [];
  const jobsTotal: number = jobsData?.total ?? 0;

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  // apartmentId typed as number | null to accommodate jobs without an apartment (BUG-T10)
  const [newJob, setNewJob] = useState({ apartmentId: 0 as number | null, assignedTo: null as number | null, priority: "normal", notes: "", adminNotes: "", address: "", scheduledDate: new Date().toISOString().split('T')[0], scheduledTime: "", ownerUserId: null as number | null, jobType: "checkout", assignedOrgId: null as number | null });
  const [editOpen, setEditOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<CleaningJob | null>(null);
  const [editForm, setEditForm] = useState({
    assignedTo: "unassigned",
    secondCleanerId: "none",
    ownerUserId: "none",
    assignedOrgId: "none",
    status: "pending",
    priority: "normal",
    notes: "",
    adminNotes: "",
    address: "",
    scheduledDate: "",
    scheduledTime: "",
    deadlineAt: "",
    jobType: "checkout",
    customFeeOverride: "",
    customCleanerPayOverride: "",
  });

  const getApartmentName = (apartmentId: number | null | undefined) => {
    if (!apartmentId) return "External Location";
    const apt = (apartments as Apartment[]).find(a => a.id === apartmentId);
    return apt?.name || `Apartment #${apartmentId}`;
  };

  const getApartmentBedrooms = (apartmentId: number | null | undefined): number | null => {
    if (!apartmentId) return null;
    const apt = (apartments as Apartment[]).find(a => a.id === apartmentId);
    return apt?.bedrooms ?? null;
  };

  const getApartmentAddress = (apartmentId: number): string => {
    const apt = (apartments as Apartment[]).find(a => a.id === apartmentId);
    if (!apt) return "";
    const building = (buildings as any[]).find(b => b.id === apt.buildingId);
    return building?.address || (apt as any).location || "";
  };

  const getAssigneeName = (userId: number | null | undefined, assignedOrgId?: number | null) => {
    if (!userId) {
      if (assignedOrgId) {
        const org = (organizationsData as any[] || []).find((o: any) => o.id === assignedOrgId);
        return org?.name ? `[Org] ${org.name}` : `Org #${assignedOrgId}`;
      }
      return "Unassigned";
    }
    const user = (users as any[]).find(u => u.id === userId);
    return user?.fullName || user?.username || `User #${userId}`;
  };

  const cleaners = (users as any[]).filter(u => (u.isActive !== false) && (u.role === "cleaner" || ((u.additionalRoles as string[] | undefined) ?? []).includes("cleaner")));
  const [hiddenCleaners, setHiddenCleaners] = useState<Set<number>>(new Set());
  const toggleHiddenCleaner = (id: number) => setHiddenCleaners(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  // "pending_no_cleaner" | "in_progress" | "completed" | "cancelled"
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(new Set(["cancelled"]));
  const toggleHiddenStatus = (key: string) => setHiddenStatuses(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const getJobTypeName = (value: string | null | undefined): string | null => {
    if (!value || value === "checkout") return null;
    const found = (cleaningJobTypesData as any[]).find((t: any) => t.value === value);
    if (found) return found.name;
    if (value === "intra_stay") return "Mid-Stay";
    return value;
  };


  const pmOwnedApartmentIds = useMemo(() => {
    if (pmClientFilter === "all") return null;
    const ownerId = Number(pmClientFilter);
    return new Set(
      (apartments as Apartment[])
        .filter(a => a.ownerUserId === ownerId)
        .map(a => a.id)
    );
  }, [pmClientFilter, apartments]);

  const filtered = (jobs as CleaningJob[])
    .filter(j => filter === "all" || j.status === filter)
    .filter(j => priorityFilter === "all" || j.priority === priorityFilter)
    .filter(j => propertyFilter === "all" || j.apartmentId === Number(propertyFilter))
    .filter(j => {
      if (pmClientFilter === "all" || pmOwnedApartmentIds === null) return true;
      if (!j.apartmentId) return false;
      return pmOwnedApartmentIds.has(j.apartmentId);
    })
    .filter(j => {
      if (!searchTerm) return true;
      const lowerSearch = searchTerm.toLowerCase();
      const aptName = getApartmentName(j.apartmentId).toLowerCase();
      const cleanerName = getAssigneeName(j.assignedTo).toLowerCase();
      return aptName.includes(lowerSearch) || cleanerName.includes(lowerSearch);
    })
    .filter(j => {
      if (viewMode !== "list") return true;
      const dateStr = j.scheduledDate ? new Date(j.scheduledDate).toISOString().split('T')[0] : null;
      if (listDateFrom && dateStr && dateStr < listDateFrom) return false;
      if (listDateTo && dateStr && dateStr > listDateTo) return false;
      return true;
    })
    .sort((a, b) => {
      if (viewMode === "list") {
        const aDate = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
        const bDate = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
        return bDate - aDate;
      }
      const pDiff = (priorityOrder[a.priority || "normal"] ?? 1) - (priorityOrder[b.priority || "normal"] ?? 1);
      if (pDiff !== 0) return pDiff;
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bDate - aDate;
    });

  const coloredCleanerIds = new Set(
    (users as any[]).filter((u: any) => (u.isActive !== false) && u.color && (u.role === "cleaner" || u.role === "maintenance")).map((u: any) => u.id)
  );
  const calendarFiltered = filtered.filter(j => {
    if (hiddenCleaners.size > 0 && j.assignedTo && hiddenCleaners.has(j.assignedTo)) return false;
    if (hiddenStatuses.size > 0) {
      if (hiddenStatuses.has("cancelled") && j.status === "cancelled" && filter !== "cancelled") return false;
      if (hiddenStatuses.has("completed") && j.status === "completed") return false;
      if (hiddenStatuses.has("in_progress") && j.status === "in_progress") return false;
      if (hiddenStatuses.has("pending_no_cleaner") && j.status === "pending" && (!j.assignedTo || !coloredCleanerIds.has(j.assignedTo))) return false;
    }
    return true;
  });

  const listFiltered = filtered.filter(j =>
    !(hiddenStatuses.has("cancelled") && j.status === "cancelled" && filter !== "cancelled")
  );

  const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: any; color: string }> = {
    pending: { label: "Pending", variant: "destructive", icon: AlertCircle, color: "bg-red-500/80" },
    in_progress: { label: "In Progress", variant: "default", icon: PlayCircle, color: "bg-blue-500/80" },
    completed: { label: "Completed", variant: "secondary", icon: CheckCircle2, color: "bg-green-500/80" },
    cancelled: { label: "Cancelled", variant: "outline", icon: XCircle, color: "bg-gray-400/80" },
  };

  const priorityConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive"; icon: any; iconClass: string }> = {
    urgent: { label: "Urgent", variant: "destructive", icon: AlertTriangle, iconClass: "text-destructive" },
    normal: { label: "Normal", variant: "default", icon: Clock, iconClass: "text-yellow-500" },
    low: { label: "Low", variant: "secondary", icon: Minus, iconClass: "text-muted-foreground" },
  };

  const owners = (users as any[]).filter(u => (u.isActive !== false) && u.role === "owner");

  const getOwnerName = (ownerUserId: number | null | undefined) => {
    if (!ownerUserId) return "Unassigned";
    const owner = (users as any[]).find(u => u.id === ownerUserId);
    return owner?.fullName || owner?.username || `User #${ownerUserId}`;
  };

  const handleAssign = async (jobId: number, userId: string) => {
    try {
      const assignedToId = userId === "unassigned" ? null : Number(userId);
      const data: any = { assignedTo: assignedToId };
      // Auto-clear secondCleanerId if it would match the new primary cleaner
      const job = (jobs as any[]).find((j: any) => j.id === jobId);
      if (assignedToId != null && job && (job as any).secondCleanerId === assignedToId) {
        data.secondCleanerId = null;
      }
      await updateJob.mutateAsync({ id: jobId, data });
      toast({ title: "Job assigned" });
    } catch (err: any) {
      toast({ title: "Failed to assign cleaner", description: err?.response?.data?.message || err?.message || "Unknown error", variant: "destructive" });
    }
  };

  const handleSecondCleanerChange = async (jobId: number, userId: string) => {
    try {
      await updateJob.mutateAsync({ id: jobId, data: { secondCleanerId: userId === "none" ? null : Number(userId) } as any });
      toast({ title: "Second cleaner updated" });
    } catch (err: any) {
      toast({ title: "Failed to update second cleaner", description: err?.response?.data?.message || err?.message || "Unknown error", variant: "destructive" });
    }
  };

  const handleOwnerChange = async (jobId: number, ownerId: string) => {
    await updateJob.mutateAsync({ id: jobId, data: { ownerUserId: ownerId === "none" ? null : Number(ownerId) } as any });
    toast({ title: "Property manager updated" });
  };

  const handleStatusChange = async (jobId: number, status: string) => {
    const updates: any = { status };
    if (status === "completed") updates.completedAt = new Date().toISOString();
    await updateJob.mutateAsync({ id: jobId, data: updates });
    toast({ title: "Status updated" });
  };

  const [revertConfirmJobId, setRevertConfirmJobId] = useState<number | null>(null);
  const [deleteConfirmJob, setDeleteConfirmJob] = useState<CleaningJob | null>(null);
  const [deleteConfirmTaskId, setDeleteConfirmTaskId] = useState<number | null>(null);
  const revertCompletion = useMutation({
    mutationFn: (jobId: number) => apiRequest("POST", `/api/admin/cleaning-jobs/${jobId}/revert-completion`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/cleaning-jobs'] });
      setRevertConfirmJobId(null);
      toast({ title: "Reverted to pending", description: "Financial records deleted." });
    },
    onError: () => {
      toast({ title: "Revert failed", variant: "destructive" });
    },
  });

  const handleDelete = async (jobId: number) => {
    await deleteJob.mutateAsync(jobId);
    toast({ title: "Job deleted" });
  };

  const handleDuplicateJob = (job: CleaningJob) => {
    const srcDate = job.scheduledDate ? new Date(job.scheduledDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    let srcTime = "";
    if (job.scheduledDate) {
      const d = new Date(job.scheduledDate);
      const h = d.getHours();
      const m = d.getMinutes();
      if (h !== 0 || m !== 0) {
        srcTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }
    setNewJob({
      apartmentId: job.apartmentId,
      assignedTo: job.assignedTo ?? null,
      priority: job.priority || "normal",
      notes: job.notes || "",
      adminNotes: job.adminNotes || "",
      address: (job as any).address || "",
      scheduledDate: srcDate,
      scheduledTime: srcTime,
      ownerUserId: job.ownerUserId ?? null,
      jobType: job.jobType || "checkout",
      assignedOrgId: (job as any).assignedOrgId ?? null,
    });
    setIsDuplicating(true);
    setShowCreateDialog(true);
  };

  const handleOpenEdit = (job: CleaningJob) => {
    setEditingJob(job);
    const jAny = job as any;
    let scheduledTime = "";
    if (job.scheduledDate) {
      const d = new Date(job.scheduledDate);
      const h = d.getHours();
      const m = d.getMinutes();
      if (h !== 0 || m !== 0) {
        scheduledTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }
    if (!scheduledTime && job.apartmentId) {
      const apt = (apartments as Apartment[]).find((a) => a.id === job.apartmentId);
      const aptOwner = apt?.ownerUserId ? (users as any[]).find(u => u.id === apt.ownerUserId) : null;
      scheduledTime =
        (apt as any)?.unitRules?.checkoutTime ??
        apt?.checkoutTime ??
        aptOwner?.pmClientRules?.defaultCheckoutTime ??
        "";
    }
    setEditForm({
      assignedTo: job.assignedTo ? String(job.assignedTo) : "unassigned",
      secondCleanerId: jAny.secondCleanerId ? String(jAny.secondCleanerId) : "none",
      ownerUserId: jAny.ownerUserId ? String(jAny.ownerUserId) : "none",
      assignedOrgId: jAny.assignedOrgId ? String(jAny.assignedOrgId) : "none",
      status: job.status,
      priority: job.priority || "normal",
      notes: job.notes || "",
      adminNotes: jAny.adminNotes || "",
      address: jAny.address || "",
      scheduledDate: job.scheduledDate ? new Date(job.scheduledDate).toISOString().split('T')[0] : "",
      scheduledTime,
      deadlineAt: jAny.deadlineAt ? new Date(jAny.deadlineAt).toISOString().split('T')[0] : "",
      jobType: jAny.jobType || "checkout",
      customFeeOverride: jAny.customFeeOverride != null ? String(jAny.customFeeOverride) : "",
      customCleanerPayOverride: jAny.customCleanerPayOverride != null ? String(jAny.customCleanerPayOverride) : "",
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingJob) return;
    const jAny = editingJob as any;
    const updates: any = {};
    const newAssignedTo = editForm.assignedTo === "unassigned" ? null : Number(editForm.assignedTo);
    if (newAssignedTo !== editingJob.assignedTo) updates.assignedTo = newAssignedTo;
    const newSecond = editForm.secondCleanerId === "none" ? null : Number(editForm.secondCleanerId);
    if (newSecond !== (jAny.secondCleanerId || null)) updates.secondCleanerId = newSecond;
    const newOwner = editForm.ownerUserId === "none" ? null : Number(editForm.ownerUserId);
    if (newOwner !== (jAny.ownerUserId || null)) updates.ownerUserId = newOwner;
    if (editForm.status !== editingJob.status) {
      updates.status = editForm.status;
      if (editForm.status === "completed" && editingJob.status !== "completed") {
        updates.completedAt = new Date().toISOString();
      }
    }
    if (editForm.priority !== (editingJob.priority || "normal")) updates.priority = editForm.priority;
    if (editForm.notes !== (editingJob.notes || "")) updates.notes = editForm.notes;
    const newAdminNotes = editForm.adminNotes.trim() || null;
    if (newAdminNotes !== (jAny.adminNotes || null)) updates.adminNotes = newAdminNotes;
    const newAddress = editForm.address.trim() || null;
    if (newAddress !== (jAny.address || null)) updates.address = newAddress;
    if (editForm.scheduledDate) {
      const timeStr = editForm.scheduledTime || "12:00";
      const newDate = new Date(`${editForm.scheduledDate}T${timeStr}:00`).toISOString();
      const oldDate = editingJob.scheduledDate ? new Date(editingJob.scheduledDate).toISOString() : "";
      if (newDate !== oldDate) updates.scheduledDate = newDate;
    } else if (editingJob.scheduledDate) {
      updates.scheduledDate = null;
    }
    if (editForm.deadlineAt) {
      const oldDeadline = jAny.deadlineAt ? new Date(jAny.deadlineAt).toISOString().split('T')[0] : "";
      if (editForm.deadlineAt !== oldDeadline) updates.deadlineAt = new Date(editForm.deadlineAt + 'T23:59:59').toISOString();
    } else if (jAny.deadlineAt) {
      updates.deadlineAt = null;
    }
    if (editForm.jobType !== (jAny.jobType || "checkout")) updates.jobType = editForm.jobType;
    const newFeeOverride = editForm.customFeeOverride.trim() ? editForm.customFeeOverride.trim() : null;
    const oldFeeOverride = jAny.customFeeOverride != null ? String(jAny.customFeeOverride) : null;
    if (newFeeOverride !== oldFeeOverride) updates.customFeeOverride = newFeeOverride;
    const newCleanerPayOverride = editForm.customCleanerPayOverride.trim() ? editForm.customCleanerPayOverride.trim() : null;
    const oldCleanerPayOverride = jAny.customCleanerPayOverride != null ? String(jAny.customCleanerPayOverride) : null;
    if (newCleanerPayOverride !== oldCleanerPayOverride) updates.customCleanerPayOverride = newCleanerPayOverride;
    const newAssignedOrgId = editForm.assignedOrgId === "none" ? null : Number(editForm.assignedOrgId);
    if (newAssignedOrgId !== (jAny.assignedOrgId ?? null)) updates.assignedOrgId = newAssignedOrgId;
    if (Object.keys(updates).length === 0) {
      toast({ title: "No changes to save" });
      return;
    }
    try {
      await updateJob.mutateAsync({ id: editingJob.id, data: updates });
      toast({ title: "Cleaning job updated" });
      setEditOpen(false);
      setEditingJob(null);
    } catch (err: any) {
      toast({ title: err.message || "Failed to update", variant: "destructive" });
    }
  };

  const pendingCount = (jobs as CleaningJob[]).filter(j => j.status === "pending").length;
  const inProgressCount = (jobs as CleaningJob[]).filter(j => j.status === "in_progress").length;
  const completedCount = (jobs as CleaningJob[]).filter(j => j.status === "completed").length;
  const urgentCount = (jobs as CleaningJob[]).filter(j => j.priority === "urgent").length;

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const calendarStartDate = useMemo(() => {
    const d = new Date(today.getFullYear(), today.getMonth() + calendarOffset, 1);
    return d;
  }, [today, calendarOffset]);

  const calendarDays = useMemo(() => {
    const year = calendarStartDate.getFullYear();
    const month = calendarStartDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: Date[] = [];
    for (let i = 0; i < daysInMonth; i++) {
      days.push(new Date(year, month, i + 1));
    }
    return days;
  }, [calendarStartDate]);

  const calendarApartments = useMemo(() => {
    const aptIds = new Set(filtered.map(j => j.apartmentId));
    return (apartments as Apartment[]).filter(a => aptIds.has(a.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered, apartments]);

  const weekDays = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const dow = now.getDay();
    const diff = (dow - weekStartDay + 7) % 7;
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - diff + weekViewOffset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekViewOffset, weekStartDay]);

  const getJobsForDay = (apartmentId: number, day: Date) => {
    return calendarFiltered.filter(j => {
      if (j.apartmentId !== apartmentId) return false;
      const jobDate = j.scheduledDate ? new Date(j.scheduledDate) : j.createdAt ? new Date(j.createdAt) : null;
      if (!jobDate) return false;
      return isSameDay(new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate()), day);
    });
  };

  const getCalendarJobColorProps = (job: CleaningJob): { className: string; style?: CSSProperties } => {
    if (job.status === "completed") return { className: "bg-green-500/80 text-white" };
    if (job.status === "in_progress") return { className: "bg-blue-500/80 text-white" };
    if (job.assignedTo) {
      const cleaner = (users as any[]).find((u: any) => u.id === job.assignedTo);
      if (cleaner?.color) return { className: "text-white", style: { backgroundColor: cleaner.color } };
    }
    if (job.priority === "urgent") return { className: "bg-red-500 text-white" };
    const s = statusConfig[job.status];
    if (s) return { className: `${s.color} text-white` };
    return { className: "bg-gray-400 text-white" };
  };

  const getCalendarTaskColorProps = (task: any): { className: string; style?: CSSProperties } => {
    if (task.status === "completed") return { className: "bg-green-400/70 text-white" };
    if (task.status === "in_progress") return { className: "bg-blue-400/70 text-white" };
    if (task.assignedTo) {
      const assignee = (users as any[]).find((u: any) => u.id === task.assignedTo);
      if (assignee?.color) return { className: "text-white", style: { backgroundColor: assignee.color } };
    }
    return { className: "bg-slate-400/70 text-white" };
  };

  const getTasksForDay = (day: Date) => {
    return (generalTasksData as any[]).filter(t => {
      if (!t.scheduledAt) return false;
      const d = new Date(t.scheduledAt);
      if (!isSameDay(new Date(d.getFullYear(), d.getMonth(), d.getDate()), day)) return false;
      if (hiddenCleaners.size > 0 && t.assignedTo && hiddenCleaners.has(t.assignedTo)) return false;
      if (hiddenStatuses.has("completed") && t.status === "completed") return false;
      if (hiddenStatuses.has("in_progress") && t.status === "in_progress") return false;
      if (hiddenStatuses.has("cancelled") && t.status === "cancelled") return false;
      return true;
    });
  };

  const calendarStartHour: number = (orgSettings as any)?.calendarStartHour ?? 6;
  const calendarEndHour: number = (orgSettings as any)?.calendarEndHour ?? 22;

  const dayHours = useMemo(() => {
    const hours: number[] = [];
    for (let h = calendarStartHour; h <= calendarEndHour; h++) hours.push(h);
    return hours;
  }, [calendarStartHour, calendarEndHour]);

  const dayViewJobs = useMemo(() => {
    return calendarFiltered.filter(j => {
      const jobDate = j.scheduledDate ? new Date(j.scheduledDate) : j.createdAt ? new Date(j.createdAt) : null;
      if (!jobDate) return false;
      return isSameDay(jobDate, dayViewDate);
    });
  }, [calendarFiltered, dayViewDate]);

  const getJobsForHour = (hour: number) => {
    return dayViewJobs.filter(j => {
      if (!j.scheduledDate) return false;
      const jobDate = new Date(j.scheduledDate);
      if (jobDate.getHours() === 0 && jobDate.getMinutes() === 0) return false;
      return jobDate.getHours() === hour;
    });
  };

  const unscheduledDayJobs = useMemo(() => {
    return dayViewJobs.filter(j => {
      if (!j.scheduledDate) return true;
      const jobDate = new Date(j.scheduledDate);
      const hour = jobDate.getHours();
      const minutes = jobDate.getMinutes();
      return hour === 0 && minutes === 0;
    });
  }, [dayViewJobs]);

  const outOfRangeDayJobs = useMemo(() => {
    return dayViewJobs.filter(j => {
      if (!j.scheduledDate) return false;
      const jobDate = new Date(j.scheduledDate);
      const h = jobDate.getHours();
      const m = jobDate.getMinutes();
      if (h === 0 && m === 0) return false; // midnight = unscheduled bucket
      return h < calendarStartHour || h > calendarEndHour;
    });
  }, [dayViewJobs, calendarStartHour, calendarEndHour]);

  const formatHour = (hour: number) => {
    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";
    return `${hour - 12} PM`;
  };

  const navigateDayView = (offset: number) => {
    setDayViewDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + offset);
      return d;
    });
  };

  const getDropMinute = (e: React.DragEvent<HTMLDivElement>): number => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relY = Math.max(0, Math.min(0.9999, (e.clientY - rect.top) / rect.height));
    return Math.floor(relY * 4) * 15; // 0, 15, 30, or 45
  };

  const handleDayViewDrop = useCallback((e: React.DragEvent, hour: number, minute: number = 0) => {
    e.preventDefault();
    setDragOverCell(null);
    const jobId = Number(e.dataTransfer.getData("text/plain"));
    if (!jobId) return;
    const d = new Date(dayViewDate);
    d.setHours(hour, minute, 0, 0);
    const timeLabel = minute > 0 ? `${formatHour(hour)}:${String(minute).padStart(2, "0")}` : formatHour(hour);
    // Pass Date object directly — InsertCleaningJob.scheduledDate is typed as Date (BUG-T8)
    updateJob.mutate({ id: jobId, data: { scheduledDate: d } }, {
      onSuccess: () => toast({ title: "Rescheduled", description: `Job moved to ${timeLabel}` }),
      onError: () => toast({ title: "Error", description: "Failed to reschedule job", variant: "destructive" }),
    });
  }, [updateJob, toast, dayViewDate, formatHour]);

  const [selectedJob, setSelectedJob] = useState<CleaningJob | null>(null);
  const [jobSheetOpen, setJobSheetOpen] = useState(false);
  const activeJob = useMemo(() => {
    if (!selectedJob) return null;
    return (jobs as any[]).find(j => j.id === selectedJob.id) || selectedJob;
  }, [selectedJob, jobs]);
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  const [expandedPhotoJobIds, setExpandedPhotoJobIds] = useState<Set<number>>(new Set());
  const [dayOutOfRangeExpanded, setDayOutOfRangeExpanded] = useState(true);
  const [weekOutOfRangeExpanded, setWeekOutOfRangeExpanded] = useState(true);
  const [orphanedDeleteOpen, setOrphanedDeleteOpen] = useState(false);
  const [orphanedDeleting, setOrphanedDeleting] = useState(false);

  const handleDeleteOrphaned = useCallback(async () => {
    setOrphanedDeleting(true);
    try {
      const res = await fetch("/api/admin/cleaning-jobs/delete-orphaned", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaning-jobs"] });
      setOrphanedDeleteOpen(false);
      toast({ title: `${data.deleted} orphaned cleaning job${data.deleted !== 1 ? "s" : ""} deleted` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setOrphanedDeleting(false);
    }
  }, [toast]);

  const handleCreateJobType = useCallback(async () => {
    const name = newJobTypeName.trim();
    if (!name) return;
    try {
      await fetch("/api/admin/cleaning-job-types", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name }),
      }).then(r => { if (!r.ok) throw new Error("Failed"); return r.json(); });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaning-job-types"] });
      setNewJobTypeName("");
      toast({ title: "Job type created" });
    } catch {
      toast({ title: "Failed to create job type", variant: "destructive" });
    }
  }, [newJobTypeName, toast]);

  const handleRenameJobType = useCallback(async (id: number) => {
    const name = renameValue.trim();
    if (!name) return;
    try {
      await fetch(`/api/admin/cleaning-job-types/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name }),
      }).then(r => { if (!r.ok) throw new Error("Failed"); return r.json(); });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaning-job-types"] });
      setRenamingTypeId(null);
      setRenameValue("");
      toast({ title: "Job type renamed" });
    } catch {
      toast({ title: "Failed to rename job type", variant: "destructive" });
    }
  }, [renameValue, toast]);

  const handleDeleteJobType = useCallback(async (id: number) => {
    try {
      await fetch(`/api/admin/cleaning-job-types/${id}`, { method: "DELETE", credentials: "include" })
        .then(r => { if (!r.ok) throw new Error("Failed"); });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaning-job-types"] });
      toast({ title: "Job type deleted" });
    } catch {
      toast({ title: "Failed to delete job type", variant: "destructive" });
    }
  }, [toast]);

  const toggleJobPhotos = useCallback((jobId: number) => {
    setExpandedPhotoJobIds(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  }, []);

  const handleCalDragStart = useCallback((e: React.DragEvent, jobId: number) => {
    e.dataTransfer.setData("text/plain", String(jobId));
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleCalDrop = useCallback((e: React.DragEvent, day: Date, hour?: number, minute: number = 0) => {
    e.preventDefault();
    setDragOverCell(null);
    const jobId = Number(e.dataTransfer.getData("text/plain"));
    if (!jobId) return;
    const target = new Date(day);
    if (hour !== undefined) {
      target.setHours(hour, minute, 0, 0);
    } else {
      target.setHours(12, 0, 0, 0);
    }
    // Pass Date object directly — InsertCleaningJob.scheduledDate is typed as Date (BUG-T8)
    updateJob.mutate({ id: jobId, data: { scheduledDate: target } }, {
      onSuccess: () => {
        const minLabel = minute > 0 ? `:${String(minute).padStart(2, "0")}` : "";
        const timeLabel = hour !== undefined ? ` at ${formatHour(hour)}${minLabel}` : "";
        toast({ title: "Rescheduled", description: `Job moved to ${day.toLocaleDateString()}${timeLabel}` });
      },
      onError: () => toast({ title: "Error", description: "Failed to reschedule job", variant: "destructive" }),
    });
  }, [updateJob, toast, formatHour]);

  const handleSaveTask = async () => {
    const taskToSave = editingTask || newTask;
    if (!taskToSave.title || (!taskToSave.address && !taskToSave.apartmentId)) {
      toast({ title: "Title and address (or unit) are required", variant: "destructive" });
      return;
    }
    let scheduledAt: string | undefined;
    if (taskToSave.scheduledAt) {
      const timeStr = taskToSave.scheduledTime || "09:00";
      scheduledAt = new Date(`${taskToSave.scheduledAt}T${timeStr}:00`).toISOString();
    }
    // Build recurrence rule + end date if recurring is enabled
    let recurrenceRule: Record<string, unknown> | null = null;
    let recurrenceEndDate: string | null = null;
    if (taskToSave.recurring) {
      const freq = taskToSave.recurrenceFrequency || "weekly";
      const rule: Record<string, unknown> = { frequency: freq, timeOfDay: taskToSave.recurrenceTimeOfDay || "09:00" };
      if (freq === "weekly" || freq === "biweekly") {
        if (!taskToSave.recurrenceDaysOfWeek || taskToSave.recurrenceDaysOfWeek.length === 0) {
          toast({ title: "Pick at least one day of week for recurrence", variant: "destructive" });
          return;
        }
        rule.daysOfWeek = taskToSave.recurrenceDaysOfWeek;
      } else if (freq === "monthly") {
        rule.dayOfMonth = taskToSave.recurrenceDayOfMonth || 1;
      }
      recurrenceRule = rule;
      if (taskToSave.recurrenceEndDate) {
        recurrenceEndDate = new Date(`${taskToSave.recurrenceEndDate}T23:59:59`).toISOString();
      }
    }

    const taskPayload = {
      title: taskToSave.title,
      description: taskToSave.description || undefined,
      assignedTo: taskToSave.assignedTo,
      scheduledAt: scheduledAt || null,
      address: taskToSave.address || null,
      apartmentId: taskToSave.apartmentId,
      taskType: taskToSave.taskType || "other",
      status: taskToSave.status,
      billable: taskToSave.billable,
      billingAmount: taskToSave.billable && taskToSave.billingAmount ? taskToSave.billingAmount : null,
      billedToUserId: taskToSave.billable ? taskToSave.billedToUserId : null,
      cleanerPayoutAmount: taskToSave.billable && taskToSave.cleanerPayoutAmount ? taskToSave.cleanerPayoutAmount : null,
      secondAssigneeId: taskToSave.secondAssigneeId || null,
      recurrenceRule,
      recurrenceEndDate,
    };
    try {
      if (editingTask?.id) {
        await updateGeneralTask.mutateAsync({ id: editingTask.id, data: taskPayload });
        toast({ title: "Task updated" });
        setEditingTask(null);
      } else {
        await createGeneralTask.mutateAsync(taskPayload);
        toast({ title: "Task created" });
        setNewTask(emptyTask);
        setShowCreateTaskDialog(false);
      }
    } catch (err: any) {
      toast({ title: err.message || "Failed to save task", variant: "destructive" });
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      await deleteGeneralTask.mutateAsync(taskId);
      toast({ title: "Task deleted" });
    } catch (err: any) {
      toast({ title: err.message || "Failed to delete task", variant: "destructive" });
    }
  };

  const filteredGeneralTasks = (generalTasksData as any[]).filter(t => {
    if (taskStatusFilter !== "all" && t.status !== taskStatusFilter) return false;
    if (taskTypeFilter !== "all" && t.taskType !== taskTypeFilter) return false;
    return true;
  });

  const taskStatusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: any }> = {
    pending: { label: "Pending", variant: "destructive", icon: AlertCircle },
    in_progress: { label: "In Progress", variant: "default", icon: PlayCircle },
    completed: { label: "Completed", variant: "secondary", icon: CheckCircle2 },
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="text-cleaning-jobs-title">
              {pageTab === "general_tasks" ? "General Tasks" : "Cleaning Jobs"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {pageTab === "general_tasks" ? "Non-apartment tasks for your cleaning and operations team." : "Auto-generated when guests complete checkout. Assign to your cleaning team."}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {pageTab === "cleaning_jobs" ? (
              <>
                <Button size="sm" onClick={() => { setNewJob({ apartmentId: 0, assignedTo: null, priority: "normal", notes: "", adminNotes: "", address: "", scheduledDate: new Date().toISOString().split('T')[0], scheduledTime: "", ownerUserId: null, jobType: "checkout", assignedOrgId: null }); setIsDuplicating(false); setShowCreateDialog(true); }} data-testid="button-create-cleaning-job">
                  <Plus className="w-4 h-4 mr-1" />
                  New Job
                </Button>
                <Button variant="outline" size="sm" onClick={() => setOrphanedDeleteOpen(true)} data-testid="button-delete-orphaned-jobs" className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10">
                  <Trash2 className="w-4 h-4" />
                  Delete Orphaned
                </Button>
                <div className="flex gap-1">
                  <Button variant={viewMode === "calendar" ? "default" : "outline"} size="sm" onClick={() => setViewMode("calendar")} data-testid="button-view-calendar">
                    <CalendarDays className="w-4 h-4 mr-1" />
                    Month
                  </Button>
                  <Button variant={viewMode === "week" ? "default" : "outline"} size="sm" onClick={() => setViewMode("week")} data-testid="button-view-week">
                    <CalendarDays className="w-4 h-4 mr-1" />
                    Week
                  </Button>
                  <Button variant={viewMode === "day" ? "default" : "outline"} size="sm" onClick={() => setViewMode("day")} data-testid="button-view-day">
                    <Clock className="w-4 h-4 mr-1" />
                    Day
                  </Button>
                  <Button variant={viewMode === "list" ? "default" : "outline"} size="sm" onClick={() => setViewMode("list")} data-testid="button-view-list">
                    <List className="w-4 h-4 mr-1" />
                    List
                  </Button>
                </div>
              </>
            ) : (
              <Button size="sm" onClick={() => { setNewTask(emptyTask); setShowCreateTaskDialog(true); }} data-testid="button-create-general-task">
                <Plus className="w-4 h-4 mr-1" />
                New Task
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-2 border-b pb-2">
          <Button variant={pageTab === "cleaning_jobs" ? "default" : "ghost"} size="sm" onClick={() => setPageTab("cleaning_jobs")} data-testid="tab-cleaning-jobs">
            <Home className="w-4 h-4 mr-1" />
            Cleaning Jobs
          </Button>
          <Button variant={pageTab === "general_tasks" ? "default" : "ghost"} size="sm" onClick={() => setPageTab("general_tasks")} data-testid="tab-general-tasks">
            <ClipboardList className="w-4 h-4 mr-1" />
            General Tasks
            {(generalTasksData as any[]).filter(t => t.status !== "completed").length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">{(generalTasksData as any[]).filter(t => t.status !== "completed").length}</Badge>
            )}
          </Button>
          <Button variant={pageTab === "settings" ? "default" : "ghost"} size="sm" onClick={() => setPageTab("settings")} data-testid="tab-cleaning-settings">
            <Settings className="w-4 h-4 mr-1" />
            Settings
          </Button>
        </div>

        {pageTab === "general_tasks" && (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {(["all", "pending", "in_progress", "completed"] as const).map(s => (
                <Button key={s} variant={taskStatusFilter === s ? "default" : "outline"} size="sm" onClick={() => setTaskStatusFilter(s)} data-testid={`filter-task-${s}`}>
                  {s === "all" ? "All" : s === "in_progress" ? "In Progress" : s.charAt(0).toUpperCase() + s.slice(1)}
                </Button>
              ))}
              <div className="border-l pl-2 flex gap-1 flex-wrap">
                {(["all", "checkin", "checkout", "unit_setup", "inspection", "laundry", "other"] as const).map(t => (
                  <Button key={t} variant={taskTypeFilter === t ? "secondary" : "ghost"} size="sm" onClick={() => setTaskTypeFilter(t)} data-testid={`filter-task-type-${t}`} className="text-xs h-7">
                    {t === "all" ? "All Types" : t === "unit_setup" ? "Unit Setup" : t.charAt(0).toUpperCase() + t.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            <RecurringSeriesCard />
            {generalTasksLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredGeneralTasks.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No general tasks</p>
                  <p className="text-sm mt-1">Create tasks for supply runs, office tasks, or off-property work.</p>
                  <Button size="sm" className="mt-4" onClick={() => { setNewTask(emptyTask); setShowCreateTaskDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> New Task
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredGeneralTasks.map((task: any) => {
                   
                  const tConfig = (taskStatusConfig[task.status as keyof typeof taskStatusConfig] ?? taskStatusConfig.pending)!;
                  const TIcon = tConfig.icon;
                  const assignedUser = (users as any[]).find(u => u.id === task.assignedTo);
                  const billedToUser = (users as any[]).find(u => u.id === task.billedToUserId);
                  const linkedApt = task.apartmentId ? (apartments as any[]).find(a => a.id === task.apartmentId) : null;
                  const TASK_TYPE_LABELS: Record<string, string> = { checkin: "Check-in", checkout: "Check-out", unit_setup: "Unit Setup", inspection: "Inspection", laundry: "Laundry", other: "Other" };
                  return (
                    <Card key={task.id} data-testid={`card-general-task-${task.id}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium truncate" data-testid={`text-task-title-${task.id}`}>{task.title}</p>
                              {task.taskType && task.taskType !== "other" && (
                                <Badge variant="outline" className="text-[10px] h-4 px-1">{TASK_TYPE_LABELS[task.taskType] || task.taskType}</Badge>
                              )}
                              {task.billable && (
                                <Badge variant="outline" className="text-[10px] h-4 px-1 text-green-700 border-green-400">
                                  <DollarSign className="w-2.5 h-2.5 mr-0.5" />
                                  {task.billingAmount ? `$${Number(task.billingAmount).toFixed(2)}` : "Billable"}
                                </Badge>
                              )}
                            </div>
                            {task.description && <p className="text-sm text-muted-foreground mt-0.5" data-testid={`text-task-desc-${task.id}`}>{task.description}</p>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Badge variant={tConfig.variant} data-testid={`badge-task-status-${task.id}`}>
                              <TIcon className="w-3 h-3 mr-1" />
                              {tConfig.label}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                          {linkedApt ? (
                            <span className="flex items-center gap-1" data-testid={`text-task-apartment-${task.id}`}>
                              <Home className="w-3 h-3" />
                              {linkedApt.name}
                            </span>
                          ) : task.address && (
                            <span className="flex items-center gap-1" data-testid={`text-task-address-${task.id}`}>
                              <MapPin className="w-3 h-3" />
                              {task.address}
                            </span>
                          )}
                          {task.scheduledAt && (
                            <span className="flex items-center gap-1" data-testid={`text-task-scheduled-${task.id}`}>
                              <Calendar className="w-3 h-3" />
                              {new Date(task.scheduledAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                          {assignedUser && (
                            <span className="flex items-center gap-1" data-testid={`text-task-assigned-${task.id}`}>
                              <User className="w-3 h-3" />
                              {assignedUser.fullName || assignedUser.username}
                            </span>
                          )}
                          {task.billable && billedToUser && (
                            <span className="flex items-center gap-1 text-green-700" data-testid={`text-task-billed-${task.id}`}>
                              <Receipt className="w-3 h-3" />
                              Bill to: {billedToUser.fullName || billedToUser.username}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <Select value={task.status} onValueChange={async (val) => { await updateGeneralTask.mutateAsync({ id: task.id, data: { status: val } }); toast({ title: "Status updated" }); }}>
                            <SelectTrigger className="h-7 text-xs w-36" data-testid={`select-task-status-${task.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditingTask({
                            ...task,
                            scheduledAt: task.scheduledAt ? new Date(task.scheduledAt).toISOString().split('T')[0] : "",
                            scheduledTime: task.scheduledAt ? new Date(task.scheduledAt).toTimeString().slice(0, 5) : "",
                            billingAmount: task.billingAmount ?? "",
                            cleanerPayoutAmount: task.cleanerPayoutAmount ?? "",
                            secondAssigneeId: task.secondAssigneeId ?? null,
                            recurring: !!task.recurrenceRule,
                            recurrenceFrequency: task.recurrenceRule?.frequency ?? "weekly",
                            recurrenceDaysOfWeek: task.recurrenceRule?.daysOfWeek ?? [],
                            recurrenceDayOfMonth: task.recurrenceRule?.dayOfMonth ?? 1,
                            recurrenceTimeOfDay: task.recurrenceRule?.timeOfDay ?? "09:00",
                            recurrenceEndDate: task.recurrenceEndDate ? new Date(task.recurrenceEndDate).toISOString().split('T')[0] : "",
                          })} data-testid={`button-edit-task-${task.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive hover:text-destructive" onClick={() => setDeleteConfirmTaskId(task.id)} data-testid={`button-delete-task-${task.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Create / Edit General Task Dialogs — share the same form via isEdit flag */}
            {([
              { open: showCreateTaskDialog, onClose: () => setShowCreateTaskDialog(false), task: newTask, setTask: setNewTask as any, isEdit: false },
              { open: !!editingTask, onClose: () => setEditingTask(null), task: editingTask || emptyTask, setTask: setEditingTask as any, isEdit: true },
            ] as const).map(({ open, onClose, task, setTask, isEdit }) => (
              <Dialog key={isEdit ? "edit" : "create"} open={open} onOpenChange={v => { if (!v) onClose(); }}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit General Task" : "New General Task"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Title *</Label>
                      <Input value={task.title || ""} onChange={e => setTask((p: any) => ({ ...p, title: e.target.value }))} placeholder="e.g. Check-in unit 4B" data-testid={`input-${isEdit ? "edit-" : ""}task-title`} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Task Type</Label>
                        <Select value={task.taskType || "other"} onValueChange={v => setTask((p: any) => ({ ...p, taskType: v }))}>
                          <SelectTrigger data-testid={`select-${isEdit ? "edit-" : ""}task-type`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="other">Other</SelectItem>
                            <SelectItem value="checkin">Check-in</SelectItem>
                            <SelectItem value="checkout">Check-out</SelectItem>
                            <SelectItem value="unit_setup">Unit Setup</SelectItem>
                            <SelectItem value="inspection">Inspection</SelectItem>
                            <SelectItem value="laundry">Laundry</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Select value={task.status || "pending"} onValueChange={v => setTask((p: any) => ({ ...p, status: v }))}>
                          <SelectTrigger data-testid={`select-${isEdit ? "edit-" : ""}task-status`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={task.description || ""} onChange={e => setTask((p: any) => ({ ...p, description: e.target.value }))} placeholder="Optional notes or instructions" rows={2} data-testid={`input-${isEdit ? "edit-" : ""}task-description`} />
                    </div>
                    <div>
                      <Label>Unit (optional)</Label>
                      <SearchableSelect
                        value={task.apartmentId ? String(task.apartmentId) : "none"}
                        onValueChange={v => setTask((p: any) => ({ ...p, apartmentId: v === "none" ? null : Number(v) }))}
                        placeholder="Link to a unit..."
                        searchPlaceholder="Search units..."
                        options={[{ value: "none", label: "No unit — use address below" }, ...(apartments as any[]).map((a: any) => ({ value: String(a.id), label: a.name }))]}
                        className="w-full"
                        data-testid={`select-${isEdit ? "edit-" : ""}task-apartment`}
                      />
                    </div>
                    {!task.apartmentId && (
                      <div>
                        <Label>Address {!task.apartmentId ? "*" : ""}</Label>
                        <Input value={task.address || ""} onChange={e => setTask((p: any) => ({ ...p, address: e.target.value }))} placeholder="Location or destination address" data-testid={`input-${isEdit ? "edit-" : ""}task-address`} />
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label>Scheduled Date</Label>
                        <Input type="date" value={task.scheduledAt || ""} onChange={e => setTask((p: any) => ({ ...p, scheduledAt: e.target.value }))} data-testid={`input-${isEdit ? "edit-" : ""}task-date`} />
                      </div>
                      <div>
                        <Label>Time</Label>
                        <Input type="time" value={task.scheduledTime || ""} onChange={e => setTask((p: any) => ({ ...p, scheduledTime: e.target.value }))} data-testid={`input-${isEdit ? "edit-" : ""}task-time`} />
                      </div>
                    </div>
                    <div>
                      <Label>Assign to (step 1)</Label>
                      <SearchableSelect
                        value={task.assignedTo ? String(task.assignedTo) : "unassigned"}
                        onValueChange={v => setTask((p: any) => ({ ...p, assignedTo: v === "unassigned" ? null : Number(v) }))}
                        placeholder="Select team member"
                        searchPlaceholder="Search team member..."
                        options={[
                          { value: "unassigned", label: "Unassigned" },
                          ...(users as any[]).filter((u: any) => ["cleaner", "staff", "maintenance", "laundry", "super_admin"].includes(u.role)).map((u: any) => ({ value: String(u.id), label: `${u.fullName || u.username} (${u.role})` })),
                        ]}
                        className="w-full"
                        data-testid={`select-${isEdit ? "edit-" : ""}task-assignee`}
                      />
                    </div>
                    <div>
                      <Label>Then hand off to (step 2, optional)</Label>
                      <SearchableSelect
                        value={task.secondAssigneeId ? String(task.secondAssigneeId) : "none"}
                        onValueChange={v => setTask((p: any) => ({ ...p, secondAssigneeId: v === "none" ? null : Number(v) }))}
                        placeholder="No handoff"
                        searchPlaceholder="Search team member..."
                        options={[
                          { value: "none", label: "No handoff" },
                          ...(users as any[]).filter((u: any) => ["cleaner", "staff", "maintenance", "laundry", "super_admin"].includes(u.role)).map((u: any) => ({ value: String(u.id), label: `${u.fullName || u.username} (${u.role})` })),
                        ]}
                        className="w-full"
                        data-testid={`select-${isEdit ? "edit-" : ""}task-second-assignee`}
                      />
                    </div>
                    <div className="border rounded-lg p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1.5 cursor-pointer">
                          <RotateCcw className="w-3.5 h-3.5 text-blue-600" />
                          Recurring task
                        </Label>
                        <Switch checked={!!task.recurring} disabled={isEdit && !!task.recurrenceParentId} title={isEdit && task.recurrenceParentId ? "Part of a recurring series. Manage it under Recurring series." : undefined} onCheckedChange={v => setTask((p: any) => ({ ...p, recurring: v }))} data-testid={`switch-${isEdit ? "edit-" : ""}task-recurring`} />
                      </div>
                      {task.recurring && (
                        <div className="space-y-3 pt-1 border-t">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Frequency</Label>
                              <Select value={task.recurrenceFrequency || "weekly"} onValueChange={v => setTask((p: any) => ({ ...p, recurrenceFrequency: v }))}>
                                <SelectTrigger data-testid={`select-${isEdit ? "edit-" : ""}recurrence-frequency`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="daily">Daily</SelectItem>
                                  <SelectItem value="weekly">Weekly</SelectItem>
                                  <SelectItem value="biweekly">Every other week</SelectItem>
                                  <SelectItem value="monthly">Monthly</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Time of day</Label>
                              <Input type="time" value={task.recurrenceTimeOfDay || "09:00"} onChange={e => setTask((p: any) => ({ ...p, recurrenceTimeOfDay: e.target.value }))} data-testid={`input-${isEdit ? "edit-" : ""}recurrence-time`} />
                            </div>
                          </div>
                          {(task.recurrenceFrequency === "weekly" || task.recurrenceFrequency === "biweekly") && (
                            <div>
                              <Label className="text-xs">Days of week</Label>
                              <div className="flex gap-1.5 flex-wrap mt-1">
                                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => {
                                  const selected = (task.recurrenceDaysOfWeek as number[] | undefined)?.includes(i);
                                  return (
                                    <Button
                                      key={i}
                                      type="button"
                                      size="sm"
                                      variant={selected ? "default" : "outline"}
                                      className="h-8 w-12 text-xs"
                                      onClick={() => setTask((p: any) => {
                                        const days: number[] = Array.isArray(p.recurrenceDaysOfWeek) ? [...p.recurrenceDaysOfWeek] : [];
                                        if (days.includes(i)) return { ...p, recurrenceDaysOfWeek: days.filter(x => x !== i) };
                                        return { ...p, recurrenceDaysOfWeek: [...days, i].sort((a, b) => a - b) };
                                      })}
                                      data-testid={`button-${isEdit ? "edit-" : ""}recurrence-day-${i}`}
                                    >
                                      {d}
                                    </Button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {task.recurrenceFrequency === "monthly" && (
                            <div>
                              <Label className="text-xs">Day of month (1–31)</Label>
                              <Input type="number" min={1} max={31} value={task.recurrenceDayOfMonth || 1} onChange={e => setTask((p: any) => ({ ...p, recurrenceDayOfMonth: Number(e.target.value) }))} data-testid={`input-${isEdit ? "edit-" : ""}recurrence-dom`} />
                            </div>
                          )}
                          <div>
                            <Label className="text-xs">Ends on (optional)</Label>
                            <Input type="date" value={task.recurrenceEndDate || ""} onChange={e => setTask((p: any) => ({ ...p, recurrenceEndDate: e.target.value }))} data-testid={`input-${isEdit ? "edit-" : ""}recurrence-end`} />
                            <p className="text-[10px] text-muted-foreground mt-1">Leave blank for indefinite recurrence.</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="border rounded-lg p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1.5 cursor-pointer">
                          <DollarSign className="w-3.5 h-3.5 text-green-600" />
                          Billable
                        </Label>
                        <Switch checked={!!task.billable} onCheckedChange={v => setTask((p: any) => ({ ...p, billable: v }))} data-testid={`switch-${isEdit ? "edit-" : ""}task-billable`} />
                      </div>
                      {task.billable && (
                        <>
                          <div>
                            <Label>Amount ($)</Label>
                            <Input type="number" min="0" step="0.01" value={task.billingAmount || ""} onChange={e => setTask((p: any) => ({ ...p, billingAmount: e.target.value }))} placeholder="0.00" data-testid={`input-${isEdit ? "edit-" : ""}task-billing-amount`} />
                          </div>
                          <div>
                            <Label>Bill to (owner)</Label>
                            <SearchableSelect
                              value={task.billedToUserId ? String(task.billedToUserId) : "none"}
                              onValueChange={v => setTask((p: any) => ({ ...p, billedToUserId: v === "none" ? null : Number(v) }))}
                              placeholder="Select owner..."
                              searchPlaceholder="Search owners..."
                              options={[{ value: "none", label: "Not assigned" }, ...(users as any[]).filter((u: any) => u.role === "owner" && u.isActive !== false).map((u: any) => ({ value: String(u.id), label: u.fullName || u.username }))]}
                              className="w-full"
                              data-testid={`select-${isEdit ? "edit-" : ""}task-billed-to`}
                            />
                          </div>
                          <div>
                            <Label>Cleaner payout ($)</Label>
                            <Input type="number" min="0" step="0.01" value={task.cleanerPayoutAmount || ""} onChange={e => setTask((p: any) => ({ ...p, cleanerPayoutAmount: e.target.value }))} placeholder="0.00 (optional)" data-testid={`input-${isEdit ? "edit-" : ""}task-cleaner-payout`} />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleSaveTask} disabled={isEdit ? updateGeneralTask.isPending : createGeneralTask.isPending} className="flex-1" data-testid={`button-${isEdit ? "save-edit" : "save"}-task`}>
                        {isEdit ? "Save Changes" : "Create Task"}
                      </Button>
                      <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        )}

        {pageTab === "settings" && (
          <div className="max-w-2xl space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Cleaning Job Types</CardTitle>
                <p className="text-sm text-muted-foreground">Customize the types of cleaning jobs used in your organization. Built-in types cannot be deleted or renamed.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {(cleaningJobTypesData as any[]).map((jt: any) => (
                  <div key={jt.id} className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30" data-testid={`card-job-type-${jt.id}`}>
                    {renamingTypeId === jt.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") handleRenameJobType(jt.id); if (e.key === "Escape") { setRenamingTypeId(null); setRenameValue(""); } }}
                          className="h-8 text-sm"
                          autoFocus
                          data-testid={`input-rename-job-type-${jt.id}`}
                        />
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600" onClick={() => handleRenameJobType(jt.id)} data-testid={`button-confirm-rename-${jt.id}`}>
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => { setRenamingTypeId(null); setRenameValue(""); }} data-testid={`button-cancel-rename-${jt.id}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <span className="flex-1 text-sm font-medium" data-testid={`text-job-type-name-${jt.id}`}>{jt.name}</span>
                        {jt.isDefault ? (
                          <Badge variant="outline" className="text-xs text-muted-foreground">Built-in</Badge>
                        ) : (
                          <>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setRenamingTypeId(jt.id); setRenameValue(jt.name); }} data-testid={`button-rename-job-type-${jt.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`button-delete-job-type-${jt.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete "{jt.name}"?</AlertDialogTitle>
                                  <AlertDialogDescription>This will remove the job type. Existing jobs with this type will retain their type value.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteJobType(jt.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`button-confirm-delete-job-type-${jt.id}`}>Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </>
                    )}
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <Input
                    value={newJobTypeName}
                    onChange={e => setNewJobTypeName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleCreateJobType(); }}
                    placeholder="New job type name..."
                    className="flex-1"
                    data-testid="input-new-job-type"
                  />
                  <Button onClick={handleCreateJobType} disabled={!newJobTypeName.trim()} data-testid="button-add-job-type">
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {pageTab === "cleaning_jobs" && (<div className="space-y-6">

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-destructive" data-testid="text-admin-pending">{pendingCount}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-primary" data-testid="text-admin-inprogress">{inProgressCount}</p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600" data-testid="text-admin-completed">{completedCount}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                <p className="text-2xl font-bold text-destructive" data-testid="text-admin-urgent">{urgentCount}</p>
              </div>
              <p className="text-xs text-muted-foreground">Urgent</p>
            </CardContent>
          </Card>
        </div>

        <LumiCleanerSuggest
          unassignedJobIds={(jobs as CleaningJob[]).filter(j => !j.assignedTo && j.status !== 'completed' && j.status !== 'cancelled').map(j => j.id)}
          cleaners={cleaners.map((c: any) => ({ id: c.id, name: c.fullName || c.username }))}
          onAssign={async (jobId, cleanerId) => {
            await updateJob.mutateAsync({ id: jobId, data: { assignedTo: cleanerId } as any });
          }}
          getApartmentName={(jobId) => {
            const job = (jobs as CleaningJob[]).find(j => j.id === jobId);
            return job ? getApartmentName(job.apartmentId) : `Job #${jobId}`;
          }}
        />

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search apartment, cleaner..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8" data-testid="search-input" />
          </div>
          <Select value={filter} onValueChange={(v) => setFilter(v as FilterStatus)}>
            <SelectTrigger className="w-[140px]" data-testid="select-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={(v) => setPriorityFilter(v as FilterPriority)}>
            <SelectTrigger className="w-[140px]" data-testid="select-filter-priority">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <SearchableSelect
            value={propertyFilter}
            onValueChange={setPropertyFilter}
            placeholder="All Properties"
            searchPlaceholder="Search property..."
            options={[
              { value: "all", label: "All Properties" },
              ...(apartments as Apartment[]).map(apt => ({ value: String(apt.id), label: apt.name }))
            ]}
            className="w-[160px] h-9 text-sm"
            data-testid="select-filter-property"
          />
          <SearchableSelect
            value={pmClientFilter}
            onValueChange={setPmClientFilter}
            placeholder="All PM Clients"
            searchPlaceholder="Search PM..."
            options={[
              { value: "all", label: "All PM Clients" },
              ...(users as any[]).filter((u: any) => u.role === "owner").map((o: any) => ({ value: String(o.id), label: `${o.fullName || o.username}${o.isActive === false ? " (inactive)" : ""}` }))
            ]}
            className="w-[160px] h-9 text-sm"
            data-testid="select-filter-pm-client"
          />
        </div>

        {viewMode === "calendar" && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  {calendarStartDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                </CardTitle>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setCalendarOffset(o => o - 1)} data-testid="button-cal-prev">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCalendarOffset(0)} data-testid="button-cal-today">
                    Today
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => setCalendarOffset(o => o + 1)} data-testid="button-cal-next">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {calendarApartments.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No cleaning jobs to display</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[700px]">
                    <div className="grid grid-cols-7 border-b">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                        <div key={d} className="p-1.5 text-center text-xs font-medium text-muted-foreground border-r last:border-r-0">{d}</div>
                      ))}
                    </div>
                    {(() => {
                      const firstDow = calendarDays[0]?.getDay() ?? 0;
                      const cells: (Date | null)[] = Array(firstDow).fill(null).concat(calendarDays);
                      while (cells.length % 7 !== 0) cells.push(null);
                      const weeks: (Date | null)[][] = [];
                      for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
                      return weeks.map((week, wi) => (
                        <div key={wi} className="grid grid-cols-7 border-b last:border-b-0">
                          {week.map((day, di) => {
                            if (!day) return <div key={`e-${di}`} className="p-1 border-r last:border-r-0 min-h-[70px] bg-muted/20" />;
                            const allDayJobs = calendarFiltered.filter(j => {
                              const jobDate = j.scheduledDate ? new Date(j.scheduledDate) : j.createdAt ? new Date(j.createdAt) : null;
                              return jobDate ? isSameDay(new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate()), day) : false;
                            });
                            const cellKey = `month-${wi}-${di}`;
                            return (
                              <div
                                key={di}
                                className={`p-1 border-r last:border-r-0 min-h-[70px] transition-colors ${isSameDay(day, today) ? "bg-primary/5" : ""} ${dragOverCell === cellKey ? "bg-primary/15 ring-1 ring-primary/40 ring-inset" : ""}`}
                                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverCell(cellKey); }}
                                onDragLeave={() => setDragOverCell(null)}
                                onDrop={e => handleCalDrop(e, day)}
                              >
                                <button
                                  className={`text-xs font-medium w-6 h-6 rounded-full flex items-center justify-center mb-0.5 hover:bg-primary/20 transition-colors ${isSameDay(day, today) ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                                  onClick={() => { setDayViewDate(day); setViewMode("day"); }}
                                  data-testid={`month-day-${day.getDate()}`}
                                >
                                  {day.getDate()}
                                </button>
                                {allDayJobs.slice(0, 3).map(job => {
                                  const cp = getCalendarJobColorProps(job);
                                  return (
                                    <div
                                      key={job.id}
                                      draggable
                                      onDragStart={e => handleCalDragStart(e, job.id)}
                                      className={`rounded px-1 py-0.5 mb-0.5 text-[9px] font-medium truncate cursor-grab active:cursor-grabbing ${cp.className}`}
                                      style={cp.style}
                                      title={`${getApartmentName(job.apartmentId)} - ${getAssigneeName(job.assignedTo, (job as any).assignedOrgId)}\n${(statusConfig[job.status] || statusConfig.pending).label}`}
                                      onClick={() => { const j = selectedJob?.id === job.id ? null : job; setSelectedJob(j); if (j) setJobSheetOpen(true); }}
                                      data-testid={`cal-job-${job.id}`}
                                    >
                                      {getApartmentName(job.apartmentId).substring(0, 10)}
                                    </div>
                                  );
                                })}
                                {allDayJobs.length > 3 && (
                                  <button
                                    className="text-[9px] text-muted-foreground hover:text-primary pl-1"
                                    onClick={() => { setDayViewDate(day); setViewMode("day"); }}
                                  >
                                    +{allDayJobs.length - 3} more
                                  </button>
                                )}
                                {getTasksForDay(day).slice(0, 2).map(task => {
                                  const cp = getCalendarTaskColorProps(task);
                                  return (
                                    <div
                                      key={`gt-${task.id}`}
                                      className={`rounded px-1 py-0.5 mb-0.5 text-[9px] font-medium truncate cursor-pointer border border-dashed border-white/40 ${cp.className}`}
                                      style={cp.style}
                                      title={`[Task] ${task.title}\n${getAssigneeName(task.assignedTo)}`}
                                      onClick={() => setEditingTask({ ...task, scheduledAt: task.scheduledAt ? new Date(task.scheduledAt).toISOString().split('T')[0] : "", scheduledTime: task.scheduledAt ? new Date(task.scheduledAt).toTimeString().slice(0, 5) : "", billingAmount: task.billingAmount ?? "", cleanerPayoutAmount: task.cleanerPayoutAmount ?? "", secondAssigneeId: (task as any).secondAssigneeId ?? null })}
                                      data-testid={`cal-task-${task.id}`}
                                    >
                                      ✓ {task.title.substring(0, 9)}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-4 p-3 text-xs text-muted-foreground border-t flex-wrap">
                {([
                  { key: "pending_no_cleaner", label: "Pending (no cleaner)", bg: "bg-red-500/80" },
                  { key: "in_progress", label: "In Progress", bg: "bg-blue-500/80" },
                  { key: "completed", label: "Completed", bg: "bg-green-500/80" },
                  { key: "cancelled", label: "Cancelled", bg: "bg-gray-400/80" },
                ] as const).map(({ key, label, bg }) => {
                  const hidden = hiddenStatuses.has(key);
                  return (
                    <button key={key} onClick={() => toggleHiddenStatus(key)} className={`flex items-center gap-1.5 transition-opacity ${hidden ? "opacity-40" : "opacity-100"}`} title={hidden ? `Show ${label}` : `Hide ${label}`}>
                      <div className={`w-3 h-3 rounded relative ${bg}`}>
                        {hidden && <div className="absolute inset-0 flex items-center justify-center"><div className="w-[11px] h-[1.5px] bg-white rotate-45" /></div>}
                      </div>
                      <span className={hidden ? "line-through" : ""}>{label}</span>
                    </button>
                  );
                })}
                {(users as any[]).filter((u: any) => (u.isActive !== false) && u.color && (u.role === "cleaner" || u.role === "maintenance")).map((u: any) => {
                  const hidden = hiddenCleaners.has(u.id);
                  return (
                    <button
                      key={u.id}
                      onClick={() => toggleHiddenCleaner(u.id)}
                      className={`flex items-center gap-1.5 transition-opacity ${hidden ? "opacity-40" : "opacity-100"}`}
                      title={hidden ? `Show ${u.fullName || u.username}` : `Hide ${u.fullName || u.username}`}
                    >
                      <div className="w-3 h-3 rounded relative" style={{ backgroundColor: u.color }}>
                        {hidden && <div className="absolute inset-0 flex items-center justify-center"><div className="w-[11px] h-[1.5px] bg-white rotate-45" /></div>}
                      </div>
                      <span className={hidden ? "line-through" : ""}>{u.fullName || u.username}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {viewMode === "week" && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  {weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – {weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </CardTitle>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setWeekViewOffset(o => o - 1)} data-testid="button-week-prev"><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => setWeekViewOffset(0)} data-testid="button-week-today">Today</Button>
                  <Button variant="outline" size="icon" onClick={() => setWeekViewOffset(o => o + 1)} data-testid="button-week-next"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <div className="min-w-[720px]">
                  {/* Header row */}
                  <div className="grid border-b" style={{ gridTemplateColumns: "52px repeat(7, 1fr)" }}>
                    <div className="border-r" />
                    {weekDays.map((day, i) => (
                      <div key={i} className={`p-2 text-center border-r last:border-r-0 ${isSameDay(day, today) ? "bg-primary/5" : ""}`}>
                        <div className="text-[10px] font-medium text-muted-foreground uppercase">
                          {day.toLocaleDateString("en-US", { weekday: "short" })}
                        </div>
                        <button
                          className={`text-sm font-semibold w-7 h-7 rounded-full flex items-center justify-center mx-auto mt-0.5 hover:bg-primary/20 transition-colors ${isSameDay(day, today) ? "bg-primary text-primary-foreground" : "text-foreground"}`}
                          onClick={() => { setDayViewDate(day); setViewMode("day"); }}
                          data-testid={`week-day-${i}`}
                        >
                          {day.getDate()}
                        </button>
                      </div>
                    ))}
                  </div>
                  {/* All-day / out-of-range row — jobs outside configured calendar hours */}
                  <WeekOutOfRangeSection
                    calendarFiltered={calendarFiltered}
                    weekDays={weekDays}
                    today={today}
                    expanded={weekOutOfRangeExpanded}
                    onToggle={() => setWeekOutOfRangeExpanded(v => !v)}
                    dragOverCell={dragOverCell}
                    onDragOver={key => setDragOverCell(key)}
                    onDragLeave={() => setDragOverCell(null)}
                    onDrop={(e, day) => handleCalDrop(e, day)}
                    onDragStart={(e, id) => handleCalDragStart(e, id)}
                    getJobColorProps={getCalendarJobColorProps}
                    getAptName={getApartmentName}
                    getAptBedrooms={getApartmentBedrooms}
                    getAssigneeName={getAssigneeName}
                    statusConfig={statusConfig}
                    onJobClick={job => { const j = selectedJob?.id === job.id ? null : job; setSelectedJob(j); if (j) setJobSheetOpen(true); }}
                    calendarStartHour={calendarStartHour}
                    calendarEndHour={calendarEndHour}
                  />
                  {/* Hour rows */}
                  <div className="overflow-y-auto" style={{ maxHeight: 520 }}>
                    {dayHours.map(hour => (
                      <div key={hour} className="grid border-b last:border-b-0" style={{ gridTemplateColumns: "52px repeat(7, 1fr)", minHeight: 48 }}>
                        <div className="border-r px-1 pt-1 shrink-0">
                          <span className="text-[10px] text-muted-foreground leading-none block text-right pr-1">{formatHour(hour)}</span>
                        </div>
                        {weekDays.map((day, i) => {
                          const hourJobs = calendarFiltered.filter(j => {
                            if (!j.scheduledDate) return false;
                            const jobDate = new Date(j.scheduledDate);
                            if (!isSameDay(new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate()), day)) return false;
                            return jobDate.getHours() === hour;
                          });
                          const hourTasks = getTasksForDay(day).filter(t => {
                            if (!t.scheduledAt) return false;
                            const h = new Date(t.scheduledAt).getHours();
                            return h === hour;
                          });
                          const cellKey = `week-${i}-${hour}`;
                          return (
                            <div
                              key={i}
                              className={`border-r last:border-r-0 relative transition-colors ${isSameDay(day, today) ? "bg-primary/5" : ""} ${dragOverCell === cellKey ? "bg-primary/15 ring-1 ring-primary/40 ring-inset" : ""}`}
                              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverCell(cellKey); }}
                              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverCell(null); }}
                              onDrop={e => handleCalDrop(e, day, hour, getDropMinute(e))}
                            >
                              <div className="absolute inset-x-0 border-t border-muted/25 pointer-events-none" style={{ top: "50%" }} />
                              <div className="p-1 relative z-10">
                              {hourJobs.map(job => {
                                const cp = getCalendarJobColorProps(job);
                                return (
                                  <div
                                    key={job.id}
                                    draggable
                                    onDragStart={e => handleCalDragStart(e, job.id)}
                                    className={`rounded px-1 py-0.5 mb-0.5 text-[10px] font-medium cursor-grab active:cursor-grabbing leading-tight ${cp.className}`}
                                    style={cp.style}
                                    title={`${getApartmentName(job.apartmentId)} — ${getAssigneeName(job.assignedTo, (job as any).assignedOrgId)}\n${(statusConfig[job.status] || statusConfig.pending).label}${job.scheduledDate ? `\n${new Date(job.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ""}`}
                                    onClick={() => { const j = selectedJob?.id === job.id ? null : job; setSelectedJob(j); if (j) setJobSheetOpen(true); }}
                                    data-testid={`week-job-${job.id}`}
                                  >
                                    <div className="truncate">{getApartmentName(job.apartmentId)}{(() => { const br = getApartmentBedrooms(job.apartmentId); return br !== null ? <span className="opacity-75 ml-1">· {br === 0 ? "Studio" : `${br}BR`}</span> : null; })()}</div>
                                    <div className="truncate opacity-75">{getAssigneeName(job.assignedTo, (job as any).assignedOrgId).split(' ')[0]}</div>
                                  </div>
                                );
                              })}
                              {hourTasks.map(task => {
                                const cp = getCalendarTaskColorProps(task);
                                return (
                                  <div
                                    key={`gt-${task.id}`}
                                    className={`rounded px-1 py-0.5 mb-0.5 text-[10px] font-medium cursor-pointer leading-tight border border-dashed border-white/40 ${cp.className}`}
                                    style={cp.style}
                                    title={`[Task] ${task.title}\n${getAssigneeName(task.assignedTo)}`}
                                    onClick={() => setEditingTask({ ...task, scheduledAt: task.scheduledAt ? new Date(task.scheduledAt).toISOString().split('T')[0] : "", scheduledTime: task.scheduledAt ? new Date(task.scheduledAt).toTimeString().slice(0, 5) : "" })}
                                    data-testid={`week-task-${task.id}`}
                                  >
                                    <div className="truncate">✓ {task.title}</div>
                                  </div>
                                );
                              })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 text-xs text-muted-foreground border-t flex-wrap">
                {([
                  { key: "pending_no_cleaner", label: "Pending (no cleaner)", bg: "bg-red-500/80" },
                  { key: "in_progress", label: "In Progress", bg: "bg-blue-500/80" },
                  { key: "completed", label: "Completed", bg: "bg-green-500/80" },
                  { key: "cancelled", label: "Cancelled", bg: "bg-gray-400/80" },
                ] as const).map(({ key, label, bg }) => {
                  const hidden = hiddenStatuses.has(key);
                  return (
                    <button key={key} onClick={() => toggleHiddenStatus(key)} className={`flex items-center gap-1.5 transition-opacity ${hidden ? "opacity-40" : "opacity-100"}`} title={hidden ? `Show ${label}` : `Hide ${label}`}>
                      <div className={`w-3 h-3 rounded relative ${bg}`}>
                        {hidden && <div className="absolute inset-0 flex items-center justify-center"><div className="w-[11px] h-[1.5px] bg-white rotate-45" /></div>}
                      </div>
                      <span className={hidden ? "line-through" : ""}>{label}</span>
                    </button>
                  );
                })}
                {(users as any[]).filter((u: any) => (u.isActive !== false) && u.color && (u.role === "cleaner" || u.role === "maintenance")).map((u: any) => {
                  const hidden = hiddenCleaners.has(u.id);
                  return (
                    <button
                      key={u.id}
                      onClick={() => toggleHiddenCleaner(u.id)}
                      className={`flex items-center gap-1.5 transition-opacity ${hidden ? "opacity-40" : "opacity-100"}`}
                      title={hidden ? `Show ${u.fullName || u.username}` : `Hide ${u.fullName || u.username}`}
                    >
                      <div className="w-3 h-3 rounded relative" style={{ backgroundColor: u.color }}>
                        {hidden && <div className="absolute inset-0 flex items-center justify-center"><div className="w-[11px] h-[1.5px] bg-white rotate-45" /></div>}
                      </div>
                      <span className={hidden ? "line-through" : ""}>{u.fullName || u.username}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {viewMode === "day" && (
          <Card>
            <CardContent className="p-0">
              <AdminCleaningDayView
                dayViewDate={dayViewDate}
                today={today}
                dayViewJobs={dayViewJobs}
                unscheduledDayJobs={unscheduledDayJobs}
                outOfRangeDayJobs={outOfRangeDayJobs}
                dayHours={dayHours}
                getApartmentName={getApartmentName}
                getApartmentBedrooms={getApartmentBedrooms}
                getAssigneeName={getAssigneeName}
                getCalendarJobColorProps={getCalendarJobColorProps}
                getCalendarTaskColorProps={getCalendarTaskColorProps}
                getJobsForHour={getJobsForHour}
                getTasksForDay={getTasksForDay}
                formatHour={formatHour}
                handleCalDragStart={handleCalDragStart}
                handleDayViewDrop={handleDayViewDrop}
                getDropMinute={getDropMinute}
                dragOverCell={dragOverCell}
                setDragOverCell={setDragOverCell}
                navigateDayView={navigateDayView}
                setDayViewDate={setDayViewDate}
                selectedJob={selectedJob}
                setSelectedJob={setSelectedJob}
                setJobSheetOpen={setJobSheetOpen}
                setEditingTask={setEditingTask}
                dayOutOfRangeExpanded={dayOutOfRangeExpanded}
                setDayOutOfRangeExpanded={setDayOutOfRangeExpanded}
                users={users as any[]}
                hiddenStatuses={hiddenStatuses}
                hiddenCleaners={hiddenCleaners}
                toggleHiddenStatus={toggleHiddenStatus}
                toggleHiddenCleaner={toggleHiddenCleaner}
              />
            </CardContent>
          </Card>
        )}

        {activeJob && (
          <Card className={`border-l-4 ${activeJob.priority === "urgent" ? "border-l-destructive" : "border-l-primary"}`} data-testid="card-selected-job">
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-sm">{getApartmentName(activeJob.apartmentId)}</span>
                    {getJobTypeName((activeJob as any).jobType) && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-amber-50 text-amber-700 border-amber-200">{getJobTypeName((activeJob as any).jobType)}</Badge>
                    )}
                    <Badge variant={(statusConfig[activeJob.status] || statusConfig.pending).variant} className="text-[10px] py-0 px-1.5">
                      {(statusConfig[activeJob.status] || statusConfig.pending).label}
                    </Badge>
                    <Badge variant={(priorityConfig[activeJob.priority || "normal"] || priorityConfig.normal).variant} className="text-[10px] py-0 px-1.5">
                      {(priorityConfig[activeJob.priority || "normal"] || priorityConfig.normal).label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{getAssigneeName(activeJob.assignedTo, (activeJob as any).assignedOrgId)}</span>
                    {activeJob.scheduledDate && (
                      <span className="flex items-center gap-1" data-testid="text-selected-job-scheduled">
                        <Calendar className="w-3 h-3" />{formatScheduledDateTime(activeJob.scheduledDate)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" onClick={() => setJobSheetOpen(true)} data-testid="button-open-job-profile">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelectedJob(null); setJobSheetOpen(false); }} data-testid="button-close-cleaning-detail">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {(viewMode === "list" || !isLoading) && viewMode === "list" && (
          <>
            <div className="flex flex-wrap items-center gap-2 pb-2">
              <span className="text-sm text-muted-foreground">Date range:</span>
              <Input
                type="date"
                value={listDateFrom}
                onChange={e => setListDateFrom(e.target.value)}
                className="w-36 h-8 text-sm"
                placeholder="From"
                data-testid="input-list-date-from"
              />
              <span className="text-sm text-muted-foreground">–</span>
              <Input
                type="date"
                value={listDateTo}
                onChange={e => setListDateTo(e.target.value)}
                className="w-36 h-8 text-sm"
                placeholder="To"
                data-testid="input-list-date-to"
              />
              {(listDateFrom || listDateTo) && (
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setListDateFrom(""); setListDateTo(""); }} data-testid="button-clear-list-dates">
                  Clear
                </Button>
              )}
            </div>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : listFiltered.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No cleaning jobs. They appear automatically when guests check out.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {listFiltered.map(job => {
                  const config = statusConfig[job.status] || statusConfig.pending;
                  const StatusIcon = config.icon;
                  const pConfig = priorityConfig[job.priority || "normal"] || priorityConfig.normal;
                  const PriorityIcon = pConfig.icon;
                  const isUrgent = job.priority === "urgent";
                  return (
                    <Card key={job.id} className={isUrgent ? "border-destructive" : ""} data-testid={`card-admin-job-${job.id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            {isUrgent && <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />}
                            <Home className="w-4 h-4 text-muted-foreground shrink-0" />
                            <Link href={`/admin/apartments/${job.apartmentId}`} className="font-medium truncate hover:underline" data-testid={`link-job-apartment-${job.id}`}>
                              {getApartmentName(job.apartmentId)}
                            </Link>
                            {(() => { const br = getApartmentBedrooms(job.apartmentId); return br !== null ? (
                              <Badge variant="outline" className="shrink-0 text-xs" data-testid={`badge-admin-bedrooms-${job.id}`}>
                                <BedDouble className="w-3 h-3 mr-1" />
                                {br === 0 ? "Studio" : `${br} BR`}
                              </Badge>
                            ) : null; })()}
                          </div>
                          <div className="flex items-center gap-1 shrink-0 flex-wrap">
                            {getJobTypeName((job as any).jobType) && (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs" variant="outline" data-testid={`badge-job-type-${job.id}`}>
                                {getJobTypeName((job as any).jobType)}
                              </Badge>
                            )}
                            <Badge variant={pConfig.variant} data-testid={`badge-admin-priority-${job.id}`}>
                              <PriorityIcon className={`w-3 h-3 mr-1 ${pConfig.iconClass}`} />
                              {pConfig.label}
                            </Badge>
                            <Badge variant={config.variant}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {config.label}
                            </Badge>
                          </div>
                        </div>
                        {job.notes && <p className="text-sm text-muted-foreground">{job.notes}</p>}
                        {(job as any).address && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`text-job-address-${job.id}`}>
                            <Home className="w-3 h-3 shrink-0" />
                            {(job as any).address}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{job.createdAt ? new Date(job.createdAt).toLocaleString() : "No date"}</span>
                          {job.scheduledDate && (
                            <span className="flex items-center gap-1" data-testid={`text-job-scheduled-${job.id}`}>
                              <Calendar className="w-3 h-3" />
                              {formatScheduledDateTime(job.scheduledDate)}
                            </span>
                          )}
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{getAssigneeName(job.assignedTo, (job as any).assignedOrgId)}</span>
                          <span className="flex items-center gap-1" data-testid={`text-job-owner-${job.id}`}>PM: {getOwnerName((job as any).ownerUserId)}</span>
                        </div>
                        {(() => {
                          const lj = getLaundryJob(job.id);
                          if (!lj) return null;
                          return (
                            <div className="flex items-center gap-2 text-xs border rounded-md px-2 py-1 bg-blue-50 border-blue-200">
                              <span className="text-blue-700 font-medium">Laundry:</span>
                              <span className="text-blue-600 capitalize">{lj.status}</span>
                              {lj.mismatch && <span className="text-destructive font-medium">⚠ Mismatch</span>}
                              {lj.takenItems?.length > 0 && <span className="text-muted-foreground">{lj.takenItems.length} items logged</span>}
                            </div>
                          );
                        })()}
                        {job.deadlineAt && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`text-admin-deadline-${job.id}`}>
                            <Calendar className="w-3 h-3" />
                            Deadline: {new Date(job.deadlineAt).toLocaleString()}
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <SearchableSelect
                            value={String(job.assignedTo || "unassigned")}
                            onValueChange={v => handleAssign(job.id, v)}
                            placeholder="Assign to..."
                            searchPlaceholder="Search cleaner..."
                            options={[
                              { value: "unassigned", label: "Unassigned" },
                              ...cleaners.filter((c: any) => c.id !== (job as any).secondCleanerId).map((c: any) => ({ value: String(c.id), label: c.fullName || c.username })),
                            ]}
                            className="w-full sm:w-[180px]"
                            data-testid={`select-assignee-${job.id}`}
                          />
                          <SearchableSelect
                            value={String((job as any).secondCleanerId || "none")}
                            onValueChange={v => handleSecondCleanerChange(job.id, v)}
                            placeholder="2nd Cleaner"
                            searchPlaceholder="Search cleaner..."
                            options={[
                              { value: "none", label: "No 2nd Cleaner" },
                              ...cleaners.filter((c: any) => c.id !== job.assignedTo).map((c: any) => ({ value: String(c.id), label: c.fullName || c.username })),
                            ]}
                            className="w-full sm:w-[180px]"
                            data-testid={`select-second-cleaner-${job.id}`}
                          />
                          <SearchableSelect
                            value={String((job as any).ownerUserId || "none")}
                            onValueChange={v => handleOwnerChange(job.id, v)}
                            placeholder="Property Manager"
                            searchPlaceholder="Search PM..."
                            options={[
                              { value: "none", label: "No Property Manager" },
                              ...owners.map((o: any) => ({ value: String(o.id), label: o.fullName || o.username })),
                            ]}
                            className="w-full sm:w-[180px]"
                            data-testid={`select-owner-${job.id}`}
                          />
                          {job.status !== "completed" && (
                            <Select value={job.status} onValueChange={v => handleStatusChange(job.id, v)}>
                              <SelectTrigger className="w-full sm:w-[150px]" data-testid={`select-status-${job.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {job.status === "completed" && (
                            <Button variant="outline" size="sm" className="text-orange-600 border-orange-300 hover:bg-orange-50" onClick={() => setRevertConfirmJobId(job.id)} data-testid={`button-revert-${job.id}`}>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />Revert
                            </Button>
                          )}
                          <Button variant="outline" size="icon" onClick={() => handleOpenEdit(job)} data-testid={`button-edit-job-${job.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="icon" onClick={() => handleDuplicateJob(job)} title="Copy to a new job" data-testid={`button-duplicate-job-${job.id}`}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteConfirmJob(job)} data-testid={`button-delete-job-${job.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                          <Button
                            variant={expandedPhotoJobIds.has(job.id) ? "secondary" : "outline"}
                            size="sm"
                            onClick={() => toggleJobPhotos(job.id)}
                            data-testid={`button-photos-${job.id}`}
                            className="gap-1"
                          >
                            <Camera className="w-3 h-3" />
                            Photos
                          </Button>
                        </div>
                        {expandedPhotoJobIds.has(job.id) && (
                          <div className="mt-3 pt-3 border-t">
                            <AdminCleaningPhotoViewer jobId={job.id} />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
            {isPaginatedView && jobsTotal > JOBS_PAGE_SIZE && (
              <div className="flex items-center justify-between pt-2 text-sm text-muted-foreground" data-testid="cleaning-jobs-pagination">
                <span>Showing {jobsPage * JOBS_PAGE_SIZE + 1}–{Math.min((jobsPage + 1) * JOBS_PAGE_SIZE, jobsTotal)} of {jobsTotal} jobs</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={jobsPage === 0} onClick={() => setJobsPage(p => p - 1)} data-testid="btn-jobs-prev">
                    <ChevronLeft className="w-4 h-4" />
                    Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={(jobsPage + 1) * JOBS_PAGE_SIZE >= jobsTotal} onClick={() => setJobsPage(p => p + 1)} data-testid="btn-jobs-next">
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Cleaning Job</DialogTitle>
            </DialogHeader>
            {editingJob && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Home className="w-4 h-4 text-muted-foreground" />
                  {getApartmentName(editingJob.apartmentId)}
                </div>
                <div>
                  <Label className="text-xs">Job Type</Label>
                  <Select value={editForm.jobType} onValueChange={(v) => setEditForm((p) => ({ ...p, jobType: v }))}>
                    <SelectTrigger data-testid="select-edit-job-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(cleaningJobTypesData as any[] || []).map((t: any) => (
                        <SelectItem key={t.value} value={t.value}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={editForm.status} onValueChange={(v) => setEditForm((p) => ({ ...p, status: v }))}>
                      <SelectTrigger data-testid="select-edit-job-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Priority</Label>
                    <Select value={editForm.priority} onValueChange={(v) => setEditForm((p) => ({ ...p, priority: v }))}>
                      <SelectTrigger data-testid="select-edit-job-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Scheduled Date & Time</Label>
                  <div className="flex gap-2">
                    <Input type="date" value={editForm.scheduledDate} onChange={(e) => setEditForm((p) => ({ ...p, scheduledDate: e.target.value }))} data-testid="input-edit-job-scheduled" className="flex-1" />
                    <Input type="time" value={editForm.scheduledTime} onChange={(e) => setEditForm((p) => ({ ...p, scheduledTime: e.target.value }))} data-testid="input-edit-job-scheduled-time" placeholder="12:00" className="w-[120px]" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Deadline</Label>
                  <Input type="date" value={editForm.deadlineAt} onChange={(e) => setEditForm((p) => ({ ...p, deadlineAt: e.target.value }))} data-testid="input-edit-job-deadline" />
                </div>
                {activeServiceOrgIds.size > 0 && (
                  <div>
                    <Label className="text-xs">Assign to Service Org</Label>
                    <Select
                      value={editForm.assignedOrgId}
                      onValueChange={(v) => setEditForm((p) => ({ ...p, assignedOrgId: v, ...(v !== "none" ? { assignedTo: "unassigned", secondCleanerId: "none" } : {}) }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Not assigned to service org" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not assigned to service org</SelectItem>
                        {(organizationsData as any[] || []).filter((o: any) => activeServiceOrgIds.has(o.id)).map((o: any) => (
                          <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {editForm.assignedOrgId === "none" && (
                <div>
                  <Label className="text-xs">Cleaner</Label>
                  <SearchableSelect
                    value={editForm.assignedTo}
                    onValueChange={(v) => setEditForm((p) => ({ ...p, assignedTo: v }))}
                    placeholder="Unassigned"
                    searchPlaceholder="Search cleaner..."
                    options={[
                      { value: "unassigned", label: "Unassigned" },
                      ...cleaners.filter((c: any) => editForm.secondCleanerId === "none" || String(c.id) !== editForm.secondCleanerId).map((c: any) => ({ value: String(c.id), label: c.fullName || c.username })),
                    ]}
                    className="w-full"
                    data-testid="select-edit-job-cleaner"
                  />
                </div>
                )}
                {editForm.assignedOrgId === "none" && (
                <div>
                  <Label className="text-xs">Second Cleaner</Label>
                  <SearchableSelect
                    value={editForm.secondCleanerId}
                    onValueChange={(v) => setEditForm((p) => ({ ...p, secondCleanerId: v }))}
                    placeholder="No 2nd Cleaner"
                    searchPlaceholder="Search cleaner..."
                    options={[
                      { value: "none", label: "No 2nd Cleaner" },
                      ...cleaners.filter((c: any) => String(c.id) !== editForm.assignedTo).map((c: any) => ({ value: String(c.id), label: c.fullName || c.username })),
                    ]}
                    className="w-full"
                    data-testid="select-edit-job-second-cleaner"
                  />
                </div>
                )}
                <div>
                  <Label className="text-xs">Property Manager</Label>
                  <SearchableSelect
                    value={editForm.ownerUserId}
                    onValueChange={(v) => setEditForm((p) => ({ ...p, ownerUserId: v }))}
                    placeholder="No Property Manager"
                    searchPlaceholder="Search PM..."
                    options={[
                      { value: "none", label: "No Property Manager" },
                      ...owners.map((o: any) => ({ value: String(o.id), label: o.fullName || o.username })),
                    ]}
                    className="w-full"
                    data-testid="select-edit-job-owner"
                  />
                </div>
                <div>
                  <Label className="text-xs">Address</Label>
                  <Input value={editForm.address} onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))} placeholder="Job location address (optional)" data-testid="input-edit-job-address" />
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea value={editForm.notes} onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Optional notes..." data-testid="input-edit-job-notes" />
                </div>
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3 space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5 text-amber-800 dark:text-amber-400">
                    <Shield className="w-3 h-3" />
                    Private Admin Notes
                  </Label>
                  <Textarea
                    value={editForm.adminNotes}
                    onChange={(e) => setEditForm((p) => ({ ...p, adminNotes: e.target.value }))}
                    placeholder="Internal notes — not visible to cleaners..."
                    rows={3}
                    data-testid="input-edit-job-admin-notes"
                    className="bg-white dark:bg-background"
                  />
                  <p className="text-[10px] text-amber-700 dark:text-amber-500">Only visible to admins. Cleaners cannot see this.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Fee Overrides</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Cleaning Fee ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.customFeeOverride}
                        onChange={(e) => setEditForm((p) => ({ ...p, customFeeOverride: e.target.value }))}
                        placeholder="Auto"
                        data-testid="input-edit-job-fee-override"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Cleaner Pay ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.customCleanerPayOverride}
                        onChange={(e) => setEditForm((p) => ({ ...p, customCleanerPayOverride: e.target.value }))}
                        placeholder="Auto"
                        data-testid="input-edit-job-cleaner-pay-override"
                      />
                    </div>
                  </div>
                  {(editingJob as any)?.status === "completed" && (
                    <p className="text-[10px] text-amber-700 dark:text-amber-500">
                      This job is completed — changing fees will update the linked financial records, cash log, and cleaner payments.
                    </p>
                  )}
                </div>
                <Button className="w-full" onClick={handleSaveEdit} disabled={updateJob.isPending} data-testid="button-save-edit-job">
                  {updateJob.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) setIsDuplicating(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{isDuplicating ? "Copy to a New Job" : "Create Cleaning Job"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!newJob.apartmentId && !newJob.address.trim()) { toast({ title: "Please select an apartment or enter an address", variant: "destructive" }); return; }
              try {
                const timeStr = newJob.scheduledTime || "12:00";
                const createdJob = await createJob.mutateAsync({
                  apartmentId: newJob.apartmentId || null,
                  assignedTo: newJob.assignedOrgId ? null : newJob.assignedTo,
                  priority: newJob.priority as "low" | "normal" | "urgent",
                  notes: newJob.notes || undefined,
                  address: newJob.address.trim() || undefined,
                  scheduledDate: newJob.scheduledDate ? new Date(`${newJob.scheduledDate}T${timeStr}:00`).toISOString() : undefined,
                  ownerUserId: newJob.ownerUserId,
                  jobType: newJob.jobType,
                  adminNotes: newJob.adminNotes.trim() || undefined,
                  assignedOrgId: newJob.assignedOrgId,
                });
                toast({ title: isDuplicating ? "Job copied successfully" : "Cleaning job created" });
                setShowCreateDialog(false);
                setIsDuplicating(false);
                if (createdJob) {
                  setSelectedJob(createdJob as CleaningJob);
                }
              } catch (err: any) {
                toast({ title: err.message || "Failed to create job", variant: "destructive" });
              }
            }} className="space-y-4" data-testid="form-create-cleaning-job">
              <div className="space-y-2">
                <Label>Job Type</Label>
                <Select value={newJob.jobType} onValueChange={v => setNewJob(p => ({ ...p, jobType: v }))} data-testid="select-create-job-type">
                  <SelectTrigger data-testid="trigger-create-job-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cleaningJobTypesData.map((jt: any) => (
                      <SelectItem key={jt.id} value={jt.value}>{jt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newJob.jobType === "intra_stay" && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Mid-stay cleaning rates apply. Billed to property manager.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Property Manager</Label>
                <SearchableSelect
                  value={newJob.ownerUserId ? String(newJob.ownerUserId) : "none"}
                  onValueChange={v => {
                    const newOwnerId = v === "none" ? null : Number(v);
                    setNewJob(p => {
                      const aptStillValid = !newOwnerId || (apartments as Apartment[]).find((a) => a.id === p.apartmentId && a.ownerUserId === newOwnerId);
                      return { ...p, ownerUserId: newOwnerId, apartmentId: aptStillValid ? p.apartmentId : 0, address: aptStillValid ? p.address : "" };
                    });
                  }}
                  placeholder="All apartments"
                  searchPlaceholder="Search PM..."
                  options={[
                    { value: "none", label: "All apartments" },
                    ...owners.map((o: any) => ({ value: String(o.id), label: o.fullName || o.username })),
                  ]}
                  className="w-full"
                  data-testid="trigger-create-owner"
                />
                {newJob.ownerUserId && (
                  <p className="text-xs text-muted-foreground" data-testid="hint-pm-filter-active">
                    Showing only this PM's apartments. Clear selection to see all.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Apartment</Label>
                <SearchableSelect
                  value={newJob.apartmentId ? String(newJob.apartmentId) : "none"}
                  onValueChange={v => {
                    if (v === "none") {
                      setNewJob(p => ({ ...p, apartmentId: 0 }));
                      return;
                    }
                    const aptId = Number(v);
                    const aptAddress = getApartmentAddress(aptId);
                    const apt = (apartments as Apartment[]).find((a) => a.id === aptId);
                    const aptOwner = apt?.ownerUserId ? (users as any[]).find(u => u.id === apt.ownerUserId) : null;
                    const effectiveCheckoutTime =
                      (apt as any)?.unitRules?.checkoutTime ??
                      apt?.checkoutTime ??
                      aptOwner?.pmClientRules?.defaultCheckoutTime ??
                      "";
                    setNewJob(p => ({
                      ...p,
                      apartmentId: aptId,
                      address: p.address || aptAddress,
                      scheduledTime: effectiveCheckoutTime || p.scheduledTime,
                    }));
                  }}
                  placeholder="Select apartment (optional)"
                  searchPlaceholder="Search apartment..."
                  options={[
                    { value: "none", label: "No apartment / external location" },
                    ...(apartments as Apartment[])
                      .filter((apt) => !newJob.ownerUserId || apt.ownerUserId === newJob.ownerUserId)
                      .map((apt) => ({ value: String(apt.id), label: apt.name })),
                  ]}
                  className="w-full"
                  data-testid="trigger-create-apartment"
                />
              </div>
              <div className="space-y-2">
                <Label>Address {!newJob.apartmentId && <span className="text-destructive">*</span>}</Label>
                <Input
                  value={newJob.address}
                  onChange={e => setNewJob(p => ({ ...p, address: e.target.value }))}
                  placeholder="Job location address"
                  data-testid="input-create-address"
                />
                {!newJob.apartmentId && !newJob.address && (
                  <p className="text-xs text-destructive">Address is required when no apartment is selected</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Scheduled Date &amp; Time</Label>
                <div className="flex gap-2">
                  <Input type="date" value={newJob.scheduledDate} onChange={e => setNewJob(p => ({ ...p, scheduledDate: e.target.value }))} data-testid="input-create-date" className="flex-1" />
                  <Input type="time" value={newJob.scheduledTime} onChange={e => setNewJob(p => ({ ...p, scheduledTime: e.target.value }))} data-testid="input-create-time" placeholder="12:00" className="w-[120px]" />
                </div>
                <p className="text-xs text-muted-foreground">Time is optional. Defaults to 12:00 PM. Fills from apartment checkout time when available.</p>
              </div>
              {activeServiceOrgIds.size > 0 && (
                <div className="space-y-2">
                  <Label>Assign to Service Org</Label>
                  <SearchableSelect
                    value={newJob.assignedOrgId ? String(newJob.assignedOrgId) : "none"}
                    onValueChange={v => setNewJob(p => ({ ...p, assignedOrgId: v === "none" ? null : Number(v), assignedTo: null }))}
                    placeholder="Internal (no service org)"
                    searchPlaceholder="Search org..."
                    options={[
                      { value: "none", label: "Internal — assign directly to a cleaner" },
                      ...(organizationsData as any[]).filter((o: any) => activeServiceOrgIds.has(o.id)).map((o: any) => ({ value: String(o.id), label: o.name })),
                    ]}
                    className="w-full"
                  />
                  {newJob.assignedOrgId && (
                    <p className="text-xs text-muted-foreground">Service org will be notified and will assign their own cleaner.</p>
                  )}
                </div>
              )}
              {!newJob.assignedOrgId && (
                <div className="space-y-2">
                  <Label>Assign Cleaner</Label>
                  <SearchableSelect
                    value={newJob.assignedTo ? String(newJob.assignedTo) : "unassigned"}
                    onValueChange={v => setNewJob(p => ({ ...p, assignedTo: v === "unassigned" ? null : Number(v) }))}
                    placeholder="Unassigned"
                    searchPlaceholder="Search cleaner..."
                    options={[
                      { value: "unassigned", label: "Unassigned" },
                      ...cleaners.map((c: any) => ({ value: String(c.id), label: c.fullName || c.username })),
                    ]}
                    className="w-full"
                    data-testid="trigger-create-cleaner"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newJob.priority} onValueChange={v => setNewJob(p => ({ ...p, priority: v }))}>
                  <SelectTrigger data-testid="trigger-create-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={newJob.notes} onChange={e => setNewJob(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes..." data-testid="input-create-notes" />
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3 space-y-1.5">
                <Label className="text-sm flex items-center gap-1.5 text-amber-800 dark:text-amber-400">
                  <Shield className="w-3.5 h-3.5" />
                  Private Admin Notes
                </Label>
                <Textarea
                  value={newJob.adminNotes}
                  onChange={e => setNewJob(p => ({ ...p, adminNotes: e.target.value }))}
                  placeholder="Internal notes — not visible to cleaners..."
                  rows={3}
                  data-testid="input-create-admin-notes"
                  className="bg-white dark:bg-background"
                />
                <p className="text-[10px] text-amber-700 dark:text-amber-500">Only visible to admins. Cleaners cannot see this.</p>
              </div>
              <Button type="submit" className="w-full" disabled={createJob.isPending} data-testid="button-submit-create-job">
                {createJob.isPending ? (isDuplicating ? "Copying..." : "Creating...") : (isDuplicating ? "Copy to New Job" : "Create Cleaning Job")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
        )}
      </div>

      <AlertDialog open={orphanedDeleteOpen} onOpenChange={setOrphanedDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete orphaned cleaning jobs?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all auto-created <strong>pending</strong> cleaning jobs that are no longer linked to a reservation (e.g. those left behind after deleting iCal reservations). Completed and in-progress jobs are never deleted. Manually created jobs are not affected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={orphanedDeleting} data-testid="button-orphaned-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOrphaned}
              disabled={orphanedDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-orphaned-delete-confirm"
            >
              {orphanedDeleting ? "Deleting..." : "Delete Orphaned Jobs"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={jobSheetOpen} onOpenChange={open => { setJobSheetOpen(open); if (!open) setSelectedJob(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col" data-testid="sheet-job-profile">
          {activeJob && (
            <>
              <div className="px-6 pt-5 pb-4 border-b bg-muted/20 pr-14">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {getJobTypeName((activeJob as any).jobType) && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{getJobTypeName((activeJob as any).jobType)}</Badge>
                  )}
                  <Badge variant={(priorityConfig[activeJob.priority || "normal"] || priorityConfig.normal).variant}>
                    {(priorityConfig[activeJob.priority || "normal"] || priorityConfig.normal).label}
                  </Badge>
                  <Badge variant={(statusConfig[activeJob.status] || statusConfig.pending).variant}>
                    {(statusConfig[activeJob.status] || statusConfig.pending).label}
                  </Badge>
                  <QaStatusBadge jobId={activeJob.id} />
                </div>
                <h2 className="text-xl font-semibold leading-tight flex items-center gap-2 flex-wrap">
                  <Link href={`/admin/apartments/${activeJob.apartmentId}`} className="hover:underline" data-testid="link-sheet-apartment">
                    {getApartmentName(activeJob.apartmentId)}
                  </Link>
                  {(() => { const br = getApartmentBedrooms(activeJob.apartmentId); return br !== null ? (
                    <Badge variant="outline" className="text-sm font-normal" data-testid="badge-sheet-bedrooms">
                      <BedDouble className="w-3.5 h-3.5 mr-1" />
                      {br === 0 ? "Studio" : `${br} BR`}
                    </Badge>
                  ) : null; })()}
                </h2>
                {(() => {
                  const addr = (activeJob as any).address || (activeJob.apartmentId ? getApartmentAddress(activeJob.apartmentId) : "");
                  if (!addr) return null;
                  return (
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(addr)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1 hover:text-primary"
                    >
                      <MapPin className="w-3.5 h-3.5 shrink-0" />{addr}
                    </a>
                  );
                })()}
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="px-6 py-5 space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{(activeJob as any).assignedOrgId && !activeJob.assignedTo ? "Service Org" : "Primary Cleaner"}</p>
                      <p className="font-medium">{getAssigneeName(activeJob.assignedTo, (activeJob as any).assignedOrgId)}</p>
                    </div>
                    {(activeJob as any).secondCleanerId && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">2nd Cleaner</p>
                        <p className="font-medium">{getAssigneeName((activeJob as any).secondCleanerId)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Property Manager</p>
                      <p className="font-medium">{getOwnerName((activeJob as any).ownerUserId)}</p>
                    </div>
                    {activeJob.scheduledDate && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Scheduled</p>
                        <p className="font-medium">{formatScheduledDateTime(activeJob.scheduledDate)}</p>
                      </div>
                    )}
                    {activeJob.deadlineAt && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Deadline</p>
                        <p className="font-medium text-destructive">{new Date(activeJob.deadlineAt).toLocaleString()}</p>
                      </div>
                    )}
                    {activeJob.createdAt && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Created</p>
                        <p className="text-muted-foreground">{new Date(activeJob.createdAt).toLocaleString()}</p>
                      </div>
                    )}
                  </div>

                  {activeJob.notes && (
                    <div className="bg-muted/40 rounded-lg px-4 py-3 text-sm">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                      <p>{activeJob.notes}</p>
                    </div>
                  )}

                  {activeJob.adminNotes && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3 text-sm">
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-400 mb-1 flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        Private Admin Notes
                      </p>
                      <p className="text-amber-900 dark:text-amber-200">{activeJob.adminNotes}</p>
                    </div>
                  )}

                  {(() => {
                    const lj = getLaundryJob(activeJob.id);
                    if (!lj) return null;
                    return (
                      <div className={`rounded-lg border px-4 py-3 text-sm space-y-2 ${lj.mismatch ? "border-destructive/60 bg-destructive/5" : "border-blue-200 bg-blue-50"}`}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Laundry Job</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={lj.status === "completed" ? "secondary" : lj.status === "in_progress" ? "default" : "outline"}>
                            {lj.status === "in_progress" ? "In Progress" : lj.status === "completed" ? "Completed" : "Pending"}
                          </Badge>
                          {lj.mismatch && (
                            <Badge variant="destructive" className="flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Mismatch
                            </Badge>
                          )}
                        </div>
                        {lj.takenItems && lj.takenItems.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-0.5">Taken to laundry</p>
                            <div className="flex flex-wrap gap-1">
                              {(lj.takenItems as { name: string; qty: number }[]).map((item, i) => (
                                <span key={i} className="text-xs bg-muted rounded px-1.5 py-0.5">{item.name} ×{item.qty}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {lj.returnedItems && lj.returnedItems.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-0.5">Returned</p>
                            <div className="flex flex-wrap gap-1">
                              {(lj.returnedItems as { name: string; qty: number }[]).map((item, i) => (
                                <span key={i} className="text-xs bg-muted rounded px-1.5 py-0.5">{item.name} ×{item.qty}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {lj.mismatch && lj.mismatchDetails && lj.mismatchDetails.length > 0 && (
                          <div className="space-y-0.5">
                            {(lj.mismatchDetails as { name: string; taken: number; returned: number }[]).map((d, i) => (
                              <p key={i} className="text-xs text-destructive">{d.name}: sent {d.taken}, returned {d.returned}</p>
                            ))}
                          </div>
                        )}
                        {lj.notes && <p className="text-xs text-muted-foreground border-t pt-1">{lj.notes}</p>}
                      </div>
                    );
                  })()}

                  <div className="space-y-2.5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Manage</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <SearchableSelect
                        value={String(activeJob.assignedTo || "unassigned")}
                        onValueChange={v => handleAssign(activeJob.id, v)}
                        placeholder="Assign..."
                        searchPlaceholder="Search cleaner..."
                        options={[
                          { value: "unassigned", label: "Unassigned" },
                          ...cleaners.filter((c: any) => c.id !== (activeJob as any).secondCleanerId).map((c: any) => ({ value: String(c.id), label: c.fullName || c.username })),
                        ]}
                        className="w-[165px]"
                        data-testid="select-sheet-assignee"
                      />
                      <SearchableSelect
                        value={String((activeJob as any).secondCleanerId || "none")}
                        onValueChange={v => handleSecondCleanerChange(activeJob.id, v)}
                        placeholder="2nd Cleaner"
                        searchPlaceholder="Search cleaner..."
                        options={[
                          { value: "none", label: "No 2nd Cleaner" },
                          ...cleaners.filter((c: any) => c.id !== activeJob.assignedTo).map((c: any) => ({ value: String(c.id), label: c.fullName || c.username })),
                        ]}
                        className="w-[165px]"
                        data-testid="select-sheet-second-cleaner"
                      />
                      {activeJob.status !== "completed" && (
                        <Select value={activeJob.status} onValueChange={v => handleStatusChange(activeJob.id, v)}>
                          <SelectTrigger className="w-[145px]" data-testid="select-sheet-status"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {activeJob.status === "completed" && (
                        <Button variant="outline" size="sm" className="text-orange-600 border-orange-300 hover:bg-orange-50" onClick={() => setRevertConfirmJobId(activeJob.id)} data-testid="button-sheet-revert">
                          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Revert to Pending
                        </Button>
                      )}
                    </div>
                  </div>

                  <Tabs defaultValue="photos" className="w-full">
                    <TabsList className="w-full" data-testid="tabs-cleaning-detail">
                      <TabsTrigger value="photos" className="flex-1" data-testid="tab-cleaning-photos">Photos</TabsTrigger>
                      <TabsTrigger value="qa" className="flex-1" data-testid="tab-cleaning-qa">QA</TabsTrigger>
                      <TabsTrigger value="costs" className="flex-1" data-testid="tab-cleaning-costs">
                        <Receipt className="w-3.5 h-3.5 mr-1" />Costs
                      </TabsTrigger>
                      <TabsTrigger value="finance" className="flex-1" data-testid="tab-cleaning-finance">
                        <DollarSign className="w-3.5 h-3.5 mr-1" />Finance
                      </TabsTrigger>
                      <TabsTrigger value="activity" className="flex-1" data-testid="tab-cleaning-activity">Activity</TabsTrigger>
                    </TabsList>
                    <TabsContent value="photos">
                      <AdminCleaningPhotoViewer jobId={activeJob.id} />
                    </TabsContent>
                    <TabsContent value="qa">
                      <QaInspectionDetail jobId={activeJob.id} />
                    </TabsContent>
                    <TabsContent value="costs">
                      <CleaningJobCostsTab
                        jobId={activeJob.id}
                        apartmentId={activeJob.apartmentId ?? null}
                        jobScheduledDate={activeJob.scheduledDate ? new Date(activeJob.scheduledDate).toISOString() : null}
                      />
                    </TabsContent>
                    <TabsContent value="finance">
                      <CleaningJobFinanceTab jobId={activeJob.id} />
                    </TabsContent>
                    <TabsContent value="activity">
                      <CleaningJobActivityTimeline jobId={activeJob.id} />
                    </TabsContent>
                  </Tabs>
                </div>
              </div>

              <div className="border-t px-6 py-3 flex items-center gap-2 bg-muted/10 flex-shrink-0 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => handleOpenEdit(activeJob)} data-testid="button-sheet-edit">
                  <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit Details
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDuplicateJob(activeJob)} data-testid="button-sheet-duplicate">
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Duplicate
                </Button>
                <Link href={`/admin/apartments/${activeJob.apartmentId}`} className="ml-auto">
                  <Button variant="ghost" size="sm" data-testid="button-sheet-view-apt">View Apartment</Button>
                </Link>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteConfirmJob(activeJob)} data-testid="button-sheet-delete">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={revertConfirmJobId !== null} onOpenChange={open => { if (!open) setRevertConfirmJobId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert to Pending?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the financial records and contractor payments that were auto-generated when this job was marked complete. Supply inventory deductions will not be reversed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => { if (revertConfirmJobId !== null) revertCompletion.mutate(revertConfirmJobId); }}
              disabled={revertCompletion.isPending}
            >
              {revertCompletion.isPending ? "Reverting..." : "Yes, Revert"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DeleteConfirmDialog
        open={deleteConfirmJob !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmJob(null); }}
        title="Delete this cleaning job?"
        description={(() => {
          if (!deleteConfirmJob) return null;
          const apt = (apartments as Apartment[]).find(a => a.id === deleteConfirmJob.apartmentId);
          const aptLabel = apt?.name || `Apartment #${deleteConfirmJob.apartmentId}`;
          const dateLabel = formatScheduledDateTime(deleteConfirmJob.scheduledDate as any);
          const status = (deleteConfirmJob as any).status as string | undefined;
          return (<>
            You're about to permanently delete the cleaning job for <strong>{aptLabel}</strong>{dateLabel ? <> scheduled for <strong>{dateLabel}</strong></> : null}.
            {status === "completed" ? <span className="block mt-2 text-destructive font-medium">This job is marked as completed. Any uploaded photos and the job history will be lost. This cannot be undone.</span> : <span className="block mt-2">This cannot be undone.</span>}
          </>);
        })()}
        confirmLabel="Delete Job"
        pending={deleteJob.isPending}
        cancelTestId="button-delete-job-cancel"
        confirmTestId="button-delete-job-confirm"
        onConfirm={async () => {
          if (!deleteConfirmJob) return;
          const id = deleteConfirmJob.id;
          await handleDelete(id);
          setDeleteConfirmJob(null);
          if (selectedJob?.id === id) { setJobSheetOpen(false); setSelectedJob(null); }
        }}
      />

      <DeleteConfirmDialog
        open={deleteConfirmTaskId !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmTaskId(null); }}
        title="Delete this task?"
        description="This will permanently delete this task and any history attached to it. If it repeats, only this day is removed: stop the series under Recurring series to keep it from coming back."
        confirmLabel="Delete Task"
        pending={deleteGeneralTask.isPending}
        cancelTestId="button-delete-task-cancel"
        confirmTestId="button-delete-task-confirm"
        onConfirm={async () => { if (deleteConfirmTaskId !== null) { await handleDeleteTask(deleteConfirmTaskId); setDeleteConfirmTaskId(null); } }}
      />
    </AdminLayout>
  );
}

// === CLEANING JOB COSTS TAB ===

const JOB_COST_CATEGORIES = [
  { value: "cleaning_supplies", label: "Cleaning Supplies" },
  { value: "supplies", label: "Supplies" },
  { value: "repairs", label: "Repairs" },
  { value: "maintenance", label: "Maintenance" },
  { value: "other", label: "Other" },
];

interface JobCostForm {
  category: string;
  description: string;
  amount: string;
  date: string;
  vendor: string;
  chargedToPm: boolean;
  paidToCleaner: boolean;
  marginPct: string;
  cleanerPayoutAmount: string;
}

const emptyJobCostForm: JobCostForm = {
  category: "other", description: "", amount: "",
  date: new Date().toISOString().split("T")[0],
  vendor: "", chargedToPm: false, paidToCleaner: false, marginPct: "",
  cleanerPayoutAmount: "",
};

const FINANCE_TYPE_LABELS: Record<string, string> = {
  check_in: "Check-in", cleaning: "Cleaning", intra_stay_cleaning: "Mid-Stay Clean",
  unit_setup: "Unit Setup", maintenance: "Maintenance", miscellaneous: "Miscellaneous", credit: "Credit",
};

function CleaningJobFinanceTab({ jobId }: { jobId: number }) {
  const { data: records = [], isLoading } = useCleaningJobFinancialRecords(jobId);
  const updateRecord = useUpdateFinancialRecord();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = (currentUser as any)?.role === "super_admin";
  const [revertRecordId, setRevertRecordId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{
    status: string; totalAmount: string; cleanerAmount: string;
    paymentMethod: string; description: string;
  }>({ status: "", totalAmount: "", cleanerAmount: "", paymentMethod: "", description: "" });

  const handleOpenEdit = (r: any) => {
    setEditingId(r.id);
    setEditForm({
      status: r.status || "pending",
      totalAmount: r.totalAmount != null ? String(Number(r.totalAmount).toFixed(2)) : "0.00",
      cleanerAmount: r.cleanerAmount != null ? String(Number(r.cleanerAmount).toFixed(2)) : "0.00",
      paymentMethod: r.paymentMethod || "cash",
      description: r.description || "",
    });
  };

  const handleSave = async () => {
    if (!editingId) return;
    try {
      await updateRecord.mutateAsync({
        id: editingId,
        data: {
          status: editForm.status as any,
          totalAmount: editForm.totalAmount,
          cleanerAmount: editForm.cleanerAmount,
          paymentMethod: editForm.paymentMethod as any,
          description: editForm.description || undefined,
        },
      });
      queryClient.invalidateQueries({ queryKey: [`/api/admin/cleaning-jobs/${jobId}/financial-records`] });
      setEditingId(null);
      toast({ title: "Record updated" });
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    }
  };

  if (isLoading) return <div className="py-6 text-center text-sm text-muted-foreground">Loading...</div>;
  if (records.length === 0) return (
    <div className="py-8 text-center text-sm text-muted-foreground">
      <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-20" />
      <p>No financial records linked to this job.</p>
    </div>
  );

  return (
    <div className="space-y-2 pt-3">
      <RevertFinancialRecordDialog
        open={revertRecordId !== null}
        recordId={revertRecordId}
        onOpenChange={(open) => { if (!open) setRevertRecordId(null); }}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: [`/api/admin/cleaning-jobs/${jobId}/financial-records`] })}
      />
      {records.map((r: any) => (
        <div key={r.id} className="border rounded-lg text-sm overflow-hidden">
          <div className="px-4 py-3 space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{FINANCE_TYPE_LABELS[r.type] ?? r.type}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${r.status === "paid" ? "bg-green-100 text-green-800" : r.status === "cancelled" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>
                  {r.status}
                </span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {r.status === "paid" && isSuperAdmin && (
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setRevertRecordId(r.id)} title="Revertir pago">
                    <RotateCcw className="w-3.5 h-3.5 text-amber-600" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => editingId === r.id ? setEditingId(null) : handleOpenEdit(r)}>
                  {editingId === r.id ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                </Button>
                <Link href="/admin/finances" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-0.5">
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </div>
            <div className="flex gap-4 text-muted-foreground flex-wrap">
              <span>Total: <span className="text-foreground font-medium">${Number(r.totalAmount || 0).toFixed(2)}</span></span>
              <span>Admin: ${Number(r.adminAmount || 0).toFixed(2)}</span>
              <span>Cleaner: <span className="text-foreground font-medium">${Number(r.cleanerAmount || 0).toFixed(2)}</span></span>
            </div>
            {r.description && <p className="text-xs text-muted-foreground">{r.description}</p>}
            <p className="text-xs text-muted-foreground">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""}</p>
          </div>

          {editingId === r.id && (
            <div className="border-t bg-muted/30 px-4 py-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={editForm.status} onValueChange={v => setEditForm(p => ({ ...p, status: v }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Payment Method</Label>
                  <Select value={editForm.paymentMethod} onValueChange={v => setEditForm(p => ({ ...p, paymentMethod: v }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="airbnb_payout">Airbnb Payout</SelectItem>
                      <SelectItem value="venmo">Venmo</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Total Amount</Label>
                  <Input type="number" step="0.01" value={editForm.totalAmount} onChange={e => setEditForm(p => ({ ...p, totalAmount: e.target.value }))} className="h-8 text-xs" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cleaner Amount</Label>
                  <Input type="number" step="0.01" value={editForm.cleanerAmount} onChange={e => setEditForm(p => ({ ...p, cleanerAmount: e.target.value }))} className="h-8 text-xs" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Input value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} className="h-8 text-xs" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditingId(null)}>Cancel</Button>
                <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={updateRecord.isPending}>
                  <Check className="w-3.5 h-3.5 mr-1" />Save
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CleaningJobCostsTab({ jobId, apartmentId, jobScheduledDate }: { jobId: number; apartmentId: number | null; jobScheduledDate: string | null }) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<any | null>(null);
  const [form, setForm] = useState<JobCostForm>(emptyJobCostForm);
  const [deleteConfirmCost, setDeleteConfirmCost] = useState<any | null>(null);

  const { data: costs = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/unit-costs/by-job", jobId],
  });

  const createCost = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/unit-costs", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-costs/by-job", jobId] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-costs"] });
      setDialogOpen(false);
      setForm(emptyJobCostForm);
      toast({ title: "Cost added" });
    },
    onError: (e: any) => toast({ title: "Failed to add cost", description: e.message, variant: "destructive" }),
  });

  const updateCost = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest("PUT", `/api/admin/unit-costs/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-costs/by-job", jobId] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-costs"] });
      setEditingCost(null);
      setDialogOpen(false);
      setForm(emptyJobCostForm);
      toast({ title: "Cost updated" });
    },
    onError: (e: any) => toast({ title: "Failed to update cost", description: e.message, variant: "destructive" }),
  });

  const deleteCost = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/unit-costs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-costs/by-job", jobId] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-costs"] });
      toast({ title: "Cost deleted" });
    },
    onError: (e: any) => toast({ title: "Failed to delete cost", description: e.message, variant: "destructive" }),
  });

  const defaultDate = jobScheduledDate
    ? new Date(jobScheduledDate).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0];

  const openAdd = () => {
    setEditingCost(null);
    setForm({ ...emptyJobCostForm, date: defaultDate });
    setDialogOpen(true);
  };

  const openEdit = (cost: any) => {
    setEditingCost(cost);
    setForm({
      category: cost.category,
      description: cost.description || "",
      amount: cost.amount,
      date: cost.date ? new Date(cost.date).toISOString().split("T")[0] : defaultDate,
      vendor: cost.vendor || "",
      chargedToPm: cost.chargedToPm ?? false,
      paidToCleaner: cost.paidToCleaner ?? false,
      marginPct: cost.marginPct || "",
      cleanerPayoutAmount: cost.cleanerPayoutAmount || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const cleanerPayout = parseFloat(form.cleanerPayoutAmount) > 0 ? form.cleanerPayoutAmount : null;
    const data = {
      apartmentId: apartmentId ?? null,
      cleaningJobId: jobId,
      category: form.category,
      description: form.description || null,
      amount: form.amount,
      date: form.date,
      vendor: form.vendor || null,
      chargedToPm: form.chargedToPm,
      paidToCleaner: cleanerPayout != null,
      marginPct: form.marginPct || null,
      cleanerPayoutAmount: cleanerPayout,
    };
    if (editingCost) {
      updateCost.mutate({ id: editingCost.id, data });
    } else {
      createCost.mutate(data);
    }
  };

  const total = costs.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

  return (
    <div className="space-y-3 pt-2">
      <div className="flex items-center justify-between">
        {costs.length > 0 && (
          <span className="text-sm text-muted-foreground">
            Total: <span className="font-semibold text-foreground">${total.toFixed(2)}</span>
          </span>
        )}
        <Button size="sm" className="bg-primary hover:bg-primary/90 ml-auto" onClick={openAdd}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Cost
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-4">Loading...</p>
      ) : costs.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground">
          <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No costs recorded for this job</p>
        </div>
      ) : (
        <div className="space-y-2">
          {costs.map((cost: any) => {
            const catLabel = JOB_COST_CATEGORIES.find(c => c.value === cost.category)?.label || cost.category;
            const baseAmt = parseFloat(cost.amount) || 0;
            const chargedAmt = cost.chargedToPm && cost.marginPct
              ? baseAmt * (1 + parseFloat(cost.marginPct) / 100)
              : baseAmt;
            return (
              <div key={cost.id} className="border rounded-lg p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <Badge variant="outline" className="text-[10px]">{catLabel}</Badge>
                      {cost.chargedToPm && <Badge className="text-[10px] bg-blue-100 text-blue-700 border-blue-200">PM Charge</Badge>}
                      {cost.paidToCleaner && <Badge className="text-[10px] bg-green-100 text-green-700 border-green-200">Cleaner</Badge>}
                    </div>
                    {cost.description && <p className="text-xs text-muted-foreground">{cost.description}</p>}
                    {cost.vendor && <p className="text-xs text-muted-foreground">{cost.vendor}</p>}
                  </div>
                  <div className="text-right shrink-0 space-y-0.5">
                    <p className="font-semibold">${baseAmt.toFixed(2)}</p>
                    {cost.chargedToPm && <p className="text-[10px] text-blue-600">PM: ${chargedAmt.toFixed(2)}</p>}
                    {cost.cleanerPayoutAmount && parseFloat(cost.cleanerPayoutAmount) > 0 && (
                      <p className="text-[10px] text-green-600">Cleaner: ${parseFloat(cost.cleanerPayoutAmount).toFixed(2)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1 mt-2">
                  <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => openEdit(cost)}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setDeleteConfirmCost(cost)}>
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={v => { if (!v) { setDialogOpen(false); setEditingCost(null); setForm(emptyJobCostForm); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingCost ? "Edit Cost" : "Add Cost"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JOB_COST_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Amount ($) *</label>
                <Input type="number" step="0.01" placeholder="0.00" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Date *</label>
                <Input type="date" value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input placeholder="What was this cost for?" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Vendor / Payee</label>
              <Input placeholder="e.g. Home Depot" value={form.vendor}
                onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} />
            </div>
            <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Billing</p>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Charge to PM</label>
                  <p className="text-xs text-muted-foreground">Pass to property manager</p>
                </div>
                <Switch checked={form.chargedToPm} onCheckedChange={v => setForm(f => ({ ...f, chargedToPm: v }))} />
              </div>
              {form.chargedToPm && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Margin (%)</label>
                  <Input type="number" step="0.1" min="0" max="100" placeholder="e.g. 15"
                    value={form.marginPct} onChange={e => setForm(f => ({ ...f, marginPct: e.target.value }))} />
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-1 block">Cleaner Payout ($)</label>
                <Input type="number" step="0.01" min="0" placeholder="0.00"
                  value={form.cleanerPayoutAmount}
                  onChange={e => setForm(f => ({ ...f, cleanerPayoutAmount: e.target.value }))} />
                <p className="text-xs text-muted-foreground mt-0.5">Leave blank if not paying a cleaner for this cost</p>
              </div>
              {form.amount && (form.chargedToPm || parseFloat(form.cleanerPayoutAmount) > 0) && (() => {
                const base = parseFloat(form.amount) || 0;
                const margin = parseFloat(form.marginPct) || 0;
                const pmCharge = form.chargedToPm ? base * (1 + margin / 100) : 0;
                const cleanerPay = parseFloat(form.cleanerPayoutAmount) || 0;
                const adminKeeps = pmCharge - cleanerPay;
                return (
                  <div className="rounded bg-muted p-2 text-xs space-y-0.5 text-muted-foreground">
                    {form.chargedToPm && <div className="flex justify-between"><span>PM charged</span><span className="font-medium text-foreground">${pmCharge.toFixed(2)}</span></div>}
                    {cleanerPay > 0 && <div className="flex justify-between"><span>Cleaner payout</span><span className="font-medium text-foreground">${cleanerPay.toFixed(2)}</span></div>}
                    {form.chargedToPm && <div className="flex justify-between border-t pt-0.5"><span>Admin keeps</span><span className="font-medium text-foreground">${adminKeeps.toFixed(2)}</span></div>}
                  </div>
                );
              })()}
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90"
              disabled={!form.amount || createCost.isPending || updateCost.isPending}
              onClick={handleSubmit}>
              {editingCost ? "Save Changes" : "Add Cost"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirmCost !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmCost(null); }}
        title="Delete this job cost?"
        description={deleteConfirmCost ? <>This will permanently delete the <strong>${parseFloat(deleteConfirmCost.amount || "0").toFixed(2)}</strong> cost{deleteConfirmCost.description ? <> ({deleteConfirmCost.description})</> : null}{deleteConfirmCost.chargedToPm ? <> — including any PM charge linked to it</> : null}. This cannot be undone.</> : null}
        confirmLabel="Delete Cost"
        pending={deleteCost.isPending}
        onConfirm={() => { if (deleteConfirmCost) { deleteCost.mutate(deleteConfirmCost.id); setDeleteConfirmCost(null); } }}
      />
    </div>
  );
}

const PHOTO_CATEGORIES = [
  { key: 'kitchen', label: 'Kitchen', icon: '🍳' },
  { key: 'living_room', label: 'Living Room', icon: '🛋️' },
  { key: 'bedrooms', label: 'Bedrooms', icon: '🛏️' },
  { key: 'bathrooms', label: 'Bathrooms', icon: '🚿' },
  { key: 'floor', label: 'Floor', icon: '🧹' },
  { key: 'fridge', label: 'Fridge', icon: '❄️' },
  { key: 'all', label: 'Other Areas', icon: '🏠' },
];

function getAuditEventConfig(log: any): { icon: React.ReactNode; label: string; detail?: string; color: string } {
  const meta = log.metadata || {};

  switch (log.action) {
    case 'created':
      return { icon: <Plus className="h-3 w-3" />, label: 'Job created', color: 'text-blue-500 bg-blue-50' };
    case 'started':
      return { icon: <PlayCircle className="h-3 w-3" />, label: 'Started cleaning', color: 'text-amber-600 bg-amber-50',
        detail: meta.startedAt ? format(new Date(meta.startedAt as string), 'HH:mm') : undefined };
    case 'completed':
      return { icon: <CheckCircle2 className="h-3 w-3" />, label: 'Marked as completed', color: 'text-green-600 bg-green-50',
        detail: meta.completedAt ? format(new Date(meta.completedAt as string), 'HH:mm') : undefined };
    case 'photo_uploaded': {
      const catLabel = (meta.category as string || '').replace(/_/g, ' ');
      return { icon: <Camera className="h-3 w-3" />, label: `Photo uploaded${catLabel ? ` · ${catLabel}` : ''}`, color: 'text-purple-600 bg-purple-50' };
    }
    case 'status_changed_to_in_progress':
      return { icon: <PlayCircle className="h-3 w-3" />, label: 'Status → In progress', color: 'text-amber-600 bg-amber-50' };
    case 'status_changed_to_completed':
      return { icon: <CheckCircle2 className="h-3 w-3" />, label: 'Status → Completed', color: 'text-green-600 bg-green-50' };
    case 'status_changed_to_pending':
      return { icon: <Clock className="h-3 w-3" />, label: 'Status → Pending', color: 'text-gray-500 bg-gray-50' };
    case 'updated': {
      const changedKeys = Object.keys(meta);
      const label = changedKeys.includes('assignedTo') ? 'Cleaner reassigned'
        : changedKeys.includes('priority') ? 'Priority changed'
        : changedKeys.includes('secondCleanerId') ? 'Second cleaner changed'
        : 'Job updated';
      return { icon: <Pencil className="h-3 w-3" />, label, color: 'text-slate-500 bg-slate-50' };
    }
    default:
      if (log.action?.startsWith('status_changed_to_')) {
        const newStatus = log.action.replace('status_changed_to_', '').replace(/_/g, ' ');
        return { icon: <AlertCircle className="h-3 w-3" />, label: `Status → ${newStatus}`, color: 'text-slate-500 bg-slate-50' };
      }
      return { icon: <Shield className="h-3 w-3" />, label: log.action.replace(/_/g, ' '), color: 'text-slate-400 bg-slate-50' };
  }
}

function CleaningJobActivityTimeline({ jobId }: { jobId: number }) {
  const { data: logs = [], isLoading } = useQuery<any[]>({
    queryKey: ['/api/admin/cleaning-jobs', jobId, 'audit'],
    queryFn: async () => {
      const res = await fetch(`/api/admin/cleaning-jobs/${jobId}/audit`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!jobId,
    refetchInterval: 30000,
  });
  if (isLoading) return <div className="text-xs text-muted-foreground py-2">Loading activity...</div>;
  if (logs.length === 0) return null;

  const sorted = [...logs].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  return (
    <div className="mt-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
        <Shield className="h-3 w-3" />
        Activity Log
      </div>
      <div className="space-y-1.5">
        {sorted.map((log: any) => {
          const { icon, label, detail, color } = getAuditEventConfig(log);
          const meta = log.metadata || {};
          const role = meta.role as string | undefined;
          const roleBadge = role === 'cleaner' ? 'Cleaner' : (role === 'super_admin' || role === 'staff') ? 'Admin' : null;
          return (
            <div key={log.id} className="flex gap-2 text-xs items-start">
              <div className={`shrink-0 rounded-full p-0.5 mt-0.5 ${color}`}>
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-foreground">{log.actorName || "System"}</span>
                {roleBadge && (
                  <span className="ml-1 text-[10px] font-medium text-muted-foreground bg-muted rounded px-1 py-0.5">{roleBadge}</span>
                )}
                <span className="text-muted-foreground mx-1">·</span>
                <span className="text-foreground/80">{label}</span>
                {detail && <span className="text-muted-foreground ml-1">at {detail}</span>}
                {log.action === 'updated' && meta && typeof meta === 'object' && Object.entries(meta).map(([k, v]: any) =>
                  typeof v === 'object' && v?.from !== undefined ? (
                    <span key={k} className="text-muted-foreground ml-1">
                      ({k.replace(/_/g, ' ')}: <span className="line-through opacity-60">{String(v.from ?? '—')}</span> → {String(v.to ?? '—')})
                    </span>
                  ) : null
                )}
              </div>
              <div className="text-muted-foreground whitespace-nowrap shrink-0 text-[10px] mt-0.5">
                {log.createdAt ? format(new Date(log.createdAt), "MMM d, HH:mm") : ""}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdminCleaningPhotoViewer({ jobId }: { jobId: number }) {
  const { data: photos = [] } = useQuery<any[]>({
    queryKey: ['/api/cleaning-jobs', jobId, 'photos'],
  });
  const { lightboxState, openLightbox, closeLightbox } = useLightbox();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('kitchen');
  const [uploading, setUploading] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState<number | null>(null);

  const photosByCategory = PHOTO_CATEGORIES.reduce((acc, cat) => {
    acc[cat.key] = (photos as any[]).filter(p => p.category === cat.key);
    return acc;
  }, {} as Record<string, any[]>);

  const hasPhotos = PHOTO_CATEGORIES.some(cat => photosByCategory[cat.key]?.length > 0);

  const allPhotos = PHOTO_CATEGORIES.flatMap(cat =>
    photosByCategory[cat.key]?.map((p: any) => ({ id: p.id, url: p.url, label: `${cat.icon} ${cat.label}` })) || []
  );

  const getGlobalIndex = (photo: any) => allPhotos.findIndex(p => p.id === photo.id);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }
      formData.append('category', selectedCategory);
      const res = await fetch(`/api/cleaning-jobs/${jobId}/photos/bulk`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Upload failed');
      queryClient.invalidateQueries({ queryKey: ['/api/cleaning-jobs', jobId, 'photos'] });
      const catLabel = PHOTO_CATEGORIES.find(c => c.key === selectedCategory)?.label || selectedCategory;
      toast({ title: `${files.length} photo${files.length !== 1 ? 's' : ''} uploaded to ${catLabel}` });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoId: number): Promise<void> => {
    setDeletingPhotoId(photoId);
    try {
      const res = await fetch(`/api/cleaning-jobs/${jobId}/photos/${photoId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      queryClient.invalidateQueries({ queryKey: ['/api/cleaning-jobs', jobId, 'photos'] });
    } catch (err: any) {
      toast({ title: 'Failed to delete photo', description: err.message, variant: 'destructive' });
    } finally {
      setDeletingPhotoId(null);
    }
  };

  return (
    <div className="space-y-2 pt-2 border-t" data-testid={`photos-admin-${jobId}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground flex items-center gap-1">
          <Camera className="w-3 h-3" /> Cleaning Photos ({photos.length})
        </Label>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs gap-1 text-muted-foreground hover:text-foreground"
          onClick={() => setShowUploader(v => !v)}
          data-testid={`button-toggle-uploader-${jobId}`}
        >
          <Plus className="w-3 h-3" />
          Add Photos
        </Button>
      </div>

      {showUploader && (
        <div className="flex items-center gap-2 flex-wrap p-2 bg-muted/40 rounded-md">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`select-photo-category-${jobId}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PHOTO_CATEGORIES.map(cat => (
                <SelectItem key={cat.key} value={cat.key}>
                  {cat.icon} {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            data-testid={`input-photo-file-${jobId}`}
          />
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`button-upload-photo-${jobId}`}
          >
            {uploading ? 'Uploading...' : 'Choose photos'}
          </Button>
        </div>
      )}

      {hasPhotos && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PHOTO_CATEGORIES.filter(cat => photosByCategory[cat.key]?.length > 0).map(cat => (
            <div key={cat.key} className="space-y-1" data-testid={`photos-admin-${cat.key}-${jobId}`}>
              <span className="text-[10px] font-medium text-muted-foreground">{cat.icon} {cat.label}</span>
              <div className="flex gap-1.5 flex-wrap">
                {photosByCategory[cat.key].map((photo: any) => (
                  <div key={photo.id} className="relative group">
                    <img
                      src={photo.url}
                      alt={cat.label}
                      className="w-16 h-16 rounded object-cover hover:ring-2 ring-primary transition-all cursor-pointer"
                      onClick={() => openLightbox(allPhotos, getGlobalIndex(photo))}
                      data-testid={`img-admin-${cat.key}-${photo.id}`}
                    />
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleDeletePhoto(photo.id)}
                      disabled={deletingPhotoId === photo.id}
                      data-testid={`button-admin-delete-photo-${photo.id}`}
                    >
                      <X className="w-2.5 h-2.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!hasPhotos && !showUploader && (
        <p className="text-xs text-muted-foreground italic">No photos yet.</p>
      )}

      {lightboxState && (
        <PhotoLightbox photos={lightboxState.photos} initialIndex={lightboxState.index} onClose={closeLightbox} />
      )}
    </div>
  );
}

// ── QA Vision helpers ──────────────────────────────────────────────────────

function QaStatusBadge({ jobId }: { jobId: number }) {
  const { data } = useQaReport(jobId);
  if (!data?.inspection) return null;

  const status = data.inspection.overallStatus as string;
  if (status === "passed") return <Badge className="bg-green-100 text-green-800 border-green-200">QA Complete ✓</Badge>;
  if (status === "in_progress") return <Badge className="bg-blue-100 text-blue-800 border-blue-200">QA In Progress</Badge>;
  if (status === "failed") return <Badge variant="destructive">QA Issues</Badge>;
  return null;
}

function QaInspectionDetail({ jobId }: { jobId: number }) {
  const { data, isLoading } = useQaReport(jobId);

  if (isLoading) return <p className="text-sm text-muted-foreground px-4 py-6 text-center">Loading QA report…</p>;
  if (!data?.inspection) return <p className="text-sm text-muted-foreground px-4 py-6 text-center">No QA inspection found for this job.</p>;

  const { inspection, zones } = data as {
    inspection: { id: number; overallStatus: string; overallScore: number | null; completedAt: string | null };
    zones: Array<{
      id: number;
      zoneName: string;
      status: string;
      cleanlinessScore: number | null;
      stagingScore: number | null;
      amenityScore: number | null;
      conditionScore: number | null;
      fixGuidance: string | null;
      scanCount: number;
      issues: Array<{ id: number; description: string; severity: string; confidence: string; category: string }>;
      photoUrls: string[];
    }>;
  };

  const statusColor = (s: string) => {
    if (s === "passed") return "text-green-700";
    if (s === "needs_attention") return "text-amber-700";
    if (s === "failed") return "text-red-700";
    return "text-gray-500";
  };

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">Overall: {inspection.overallStatus.replace("_", " ")}</span>
        {inspection.overallScore !== null && (
          <span className="text-muted-foreground">Score: {inspection.overallScore}/100</span>
        )}
      </div>

      {zones.map(zone => (
        <div key={zone.id} className="border rounded-md overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-muted/30">
            <span className="text-sm font-medium capitalize">{zone.zoneName.replace(/_/g, " ")}</span>
            <span className={`text-xs font-medium ${statusColor(zone.status)}`}>{zone.status.replace("_", " ")}</span>
          </div>
          {zone.issues.length > 0 && (
            <div className="px-3 py-2 space-y-1">
              {zone.issues.map(issue => (
                <div key={issue.id} className="flex gap-2 items-start text-xs">
                  <Badge className={`shrink-0 text-xs ${issue.severity === "high" ? "bg-red-100 text-red-800" : issue.severity === "medium" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}`}>
                    {issue.severity}
                  </Badge>
                  <span className="text-gray-700">{issue.description}</span>
                </div>
              ))}
            </div>
          )}
          {zone.photoUrls?.length > 0 && (
            <div className="flex gap-1 px-3 py-2 flex-wrap">
              {zone.photoUrls.map((url, i) => (
                <img key={i} src={url} alt={`Zone photo ${i + 1}`} className="w-16 h-16 object-cover rounded border" />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
